// src/components/proposals/BronzeTemplate.jsx
//
// Bronze Sentinel proposal template — React port of
// Proposal-Template-Bronze.dc.html (Claude Designs standalone, 19 Jul 2026).
//
// STRUCTURAL PORT, NOT A REDESIGN. Every colour, size, weight, padding and
// border below is copied from the source document. If you are tempted to
// "tidy" a value, don't — Craig tuned these against printed output.
//
// Three differences from the source, all deliberate:
//
//  1. LAYOUT IS TABLES, NOT CSS GRID. The PDF is rendered by DocRaptor's
//     default Prince engine, which does not implement CSS grid. Every
//     `display:grid` and every `justify-content:space-between` flex row in
//     the original is a <table> here. The two real <table>s in the source
//     (tier comparison, response times) stay tables.
//  2. Page visibility is React conditional rendering, not the source's
//     `data-page-key` + direct DOM display toggling. The SETUP.md warning
//     about <sc-if> nesting pages does not apply to React.
//  3. Empty intro paragraphs render nothing instead of the source's
//     "✏️ Paragraph 1 — ..." coaching placeholder. Those placeholders are
//     an authoring aid; they must never reach a customer PDF. They still
//     show in edit mode.
//
// The print CSS in getProposalPrintCss() is copied verbatim from
// REFINEMENTS.md §4 and §5 — do not edit it without re-testing a real PDF.
import React from "react";
import { computeBlockSubtotal, lineItemTotal, fmtMoney,
         chunkFoundationalBlocks,
         DEFAULT_FOUNDATIONAL_PRICING_NOTE } from "./foundationalBlocks";
import { packSegments } from "./pagePacking";
/* Bronze still keeps its own copies of the page furniture (see the note at the
   top of proposalChrome.jsx — rewiring it is a separate job that needs real
   PDFs to re-test). This is a single targeted import rather than a fourth copy
   of a text input that has already had the "010" bug fixed in it once. */
import { MoneyInput } from "./proposalChrome";

/* ══════════════════════════════════════════════════════════════════════
   Palette — SETUP.md "Colour Palette"
   ══════════════════════════════════════════════════════════════════════ */
export const PROPOSAL_COLORS = {
  primary:   "#1a1a1a",
  teal:      "#0b80a5",
  slate:     "#2a4365",
  lightBg:   "#f8fafc",
  border:    "#e5e7eb",
  textMuted: "#6b7280",
  footer:    "#d1d5db",
};

/* The nine toggleable pages, in document order. The cover is deliberately
   absent — SETUP.md: "The cover page is never toggleable." */
export const PAGE_KEYS = [
  ["showIntro",         "Introduction Letter"],
  ["showWhatHeard",     "What We Heard"],
  ["showWhatFound",     "What We've Found"],
  ["showRecommend",     "Our Recommendation"],
  ["showComparison",    "Service Tier Comparison"],
  ["showInvestment",    "Your IT Investment"],
  ["showFoundational",  "Foundational Upgrade"],
  ["showResponseTerms", "Response Times & Terms"],
  ["showWhyUs",         "Why Choose Datatech"],
];

export const PLAN_OPTIONS = [
  "Copper Defender",
  "Bronze Sentinel",
  "Silver Guardian",
  "Gold Fortress",
];

export const MAX_LIST_ITEMS = 6;

/* Defaults mirror the source's state initialisers (lines 1011-1051). */
export function DEFAULT_BRONZE_CONTENT() {
  return {
    customerName:      "Customer Company Name",
    customerFullName:  "Contact Full Name",
    customerContact:   "First Name",
    // Not rendered anywhere in the document. DocuSign cannot address an
    // envelope without it, and the content model captured no email at all.
    customerEmail:     "",
    proposalDate:      "Month Year",
    salesRepName:      "Your Name",
    salesRepTitle:     "Your Title",

    recommendedPlan:   "Bronze Sentinel",

    deviceCount:       5,
    userCount:         5,
    perDeviceRate:     49,
    perUserRate:       0,
    onboardingFee:     625,
    intunePerDevRate:  110,
    intuneSetupFee:    599,
    // Waive the two fixed setup fees without destroying their amounts.
    // The numbers below stay whatever they are; this only decides
    // whether they are charged. Gold's template seeds this true.
    waiveSetupFees:    false,

    /* Copper Defender and the cross-tier Bronze grouping. Both are supplementary
       DEVICE groupings billed on the same contract as the tier itself.
       copperRate carries its real amount rather than 0 for the same reason the
       setup fees do: a zeroed rate throws the number away, and turning the
       toggle on later would then quote $0/device. */
    addOnCopper:        false,
    copperDeviceCount:  0,
    copperRate:         15,
    addOnBronze:        false,
    bronzeDeviceCount:  0,
    addOnNetwork:       false,
    networkDeviceCount: 5,
    // Freeform description of what the Network Monitor add-on actually covers.
    // Purely descriptive — no pricing effect — and printed only when the add-on
    // is included, since a device list under an excluded service reads as a
    // service the customer is getting.
    networkMonitorDevicesCovered: "",
    addOnBackup:        false,
    addOnSpamThreat:    false,
    addOnEmailSignature: false,
    showM365Standard:   true,
    m365PremiumQty:     null,
    // "premium" | "premium-copilot" — see M365_PLANS. Business Premium is the
    // default because it is what almost every customer is quoted.
    m365PlanChoice:     "premium",
    additionalM365Items: [],

    /* 3CX Phone System — off by default; the whole section only exists on a
       proposal that includes phones. `threeCXHostingFee` starts EMPTY rather
       than 0: these are free-text money fields, and a field seeded with a
       literal zero is what made typing "10" read "010". The SIP trunk price
       does seed a real figure — $90/mth ex GST — because that is a meaningful
       starting rate rather than an "unset" placeholder. It is an ASSUMED
       default from Craig's example, not a confirmed standing rate, and is
       editable per proposal. */
    include3CX:                 false,
    threeCXHostingFee:          "",
    threeCXSipTrunkDescription: "",
    threeCXSipTrunkPrice:       90,
    additionalRows:     [],

    introPara1: "",
    introPara2: "",
    introPara3: "",
    challenges: [],
    findings:   [],
    // Foundational Upgrade blocks: named, separately-quoted groups of priced
    // line items. Replaced the old title/description-only `projects` list.
    foundationalBlocks: [],
    // Estimate-only caveat printed under the Foundational intro. Seeded from
    // the shared constant so the three tiers cannot state it differently.
    foundationalPricingNote: DEFAULT_FOUNDATIONAL_PRICING_NOTE,

    showIntro:         true,
    showWhatHeard:     true,
    showWhatFound:     true,
    showWhatFoundPage: true,
    showRecommend:     true,
    showComparison:    true,
    showInvestment:    true,
    showResponseTerms: true,
    showFoundational:  true,
    showWhyUs:         true,

    // Stored shape holds an ID, not the image. The actual base64 lives once per
    // proposal in `proposal.coverImages` — snapshots deep-clone `content`, so
    // holding the image here would duplicate ~300-500KB per snapshot and push
    // the shared state blob toward its ceiling. ProposalsTab.hydrateContent()
    // resolves the id into `coverImage` at render time.
    coverImageId: null,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   Pricing — a direct transcription of renderVals() lines 1385-1421.
   Exported so the builder shell can show the same totals without
   re-deriving them (and drifting).
   ══════════════════════════════════════════════════════════════════════ */
/* The Microsoft 365 plans the customer picks between — mutually exclusive, one
   charged at a time. EXPORTED so Silver imports the rates rather than holding a
   third transcription of them; a rate that changes in two files out of three is
   invisible until a customer reads two documents side by side.

   `premium` carries the rates that were already live: 39.48/mth against a
   32.90/mth annual equivalent. Located in the existing maths, not re-supplied —
   they are what produced the "$395/yr" saving already printed on the page. */
export const M365_PLANS = {
  premium: {
    key: "premium",
    label: "Microsoft 365 Business Premium",
    monthly: 39.48,
    annual: 32.90,
  },
  "premium-copilot": {
    key: "premium-copilot",
    label: "Microsoft 365 Business Premium + Copilot",
    monthly: 57.48,
    annual: 47.90,
  },
};

/* Does this row's annual rate look like a yearly total in the wrong field?
   Exported so all three editors can SAY the row is excluded rather than drop it
   in silence — an exclusion nobody can see is how a wrong figure survives to a
   customer. Flagged only when both rates are present; a blank annual rate is
   ordinary and is not an error. */
export function m365ItemRateSuspect(r) {
  const m = parseFloat(r && r.monthlyUnitPrice), a = parseFloat(r && r.annualUnitPrice);
  return Number.isFinite(m) && Number.isFinite(a) && a > m;
}

export function computeBronzeTotals(content) {
  const c = content || {};
  const deviceCount = num(c.deviceCount, 5);
  const userCount   = num(c.userCount, 5);
  const perDevRate  = num(c.perDeviceRate, 0);
  const perUsrRate  = num(c.perUserRate, 0);
  const onboarding  = num(c.onboardingFee, 0);
  const intunePerDevRate = num(c.intunePerDevRate, 0);
  const intuneSetup = num(c.intuneSetupFee, 0);
  const plan = c.recommendedPlan || "Bronze Sentinel";

  const isHighTier  = plan === "Gold Fortress" || plan === "Silver Guardian";
  const networkRate = isHighTier ? 30 : 50;
  const networkDeviceCount = num(c.networkDeviceCount, 0);
  const networkCost = c.addOnNetwork ? networkDeviceCount * networkRate : 0;

  /* Copper Defender — a supplementary device grouping at its own rate,
     offered on every tier. Its own rate, because Copper is a cheaper class of
     cover ($15) and not a discount on the tier's device rate. */
  const copperRate = num(c.copperRate, 15);
  const copperDeviceCount = num(c.copperDeviceCount, 0);
  const copperCost = c.addOnCopper ? copperDeviceCount * copperRate : 0;

  /* Bronze devices carried on a Silver or Gold proposal — "10 on Gold plus 2
     on Bronze", one proposal and one Accept instead of two.
     ⚠ Charged at perDeviceRate, the SAME rate as the tier's own devices, and
     deliberately following any per-proposal edit to it. Kept as its own line
     rather than folded into the device count so the two groupings stay
     distinguishable on the contract and the invoice.
     ⚠ Gated on the TIER here and not only in the UI: Bronze IS the base tier,
     so a Bronze proposal carrying Bronze extras is a contradiction. Gating in
     the maths keeps the row and the total agreeing no matter what a stored
     record says — the lesson from Gold's missing per-user row. */
  const isBronzeTier = plan === "Bronze Sentinel";
  const bronzeDeviceCount = num(c.bronzeDeviceCount, 0);
  const bronzeAddOnCost = c.addOnBronze && !isBronzeTier ? bronzeDeviceCount * perDevRate : 0;
  const backupRate  = 9;   // M365 End User Backup, per user/mth
  const backupCost  = c.addOnBackup ? userCount * backupRate : 0;

  // Advanced AI Spam & Threat Protection. Gold-only discount — deliberately NOT
  // isHighTier, which also matches Silver. Silver is specified at the standard
  // $10 here, unlike the network add-on where it shares Gold's rate.
  const spamThreatRate = plan === "Gold Fortress" ? 6 : 10;
  const spamThreatCost = c.addOnSpamThreat ? userCount * spamThreatRate : 0;

  // Email Signature Management — flat across every tier.
  const emailSignatureRate = 10;
  const emailSignatureCost = c.addOnEmailSignature ? userCount * emailSignatureRate : 0;

  // m365PremiumQty null => fall back to userCount (source uses ??)
  const m365Qty = c.m365PremiumQty == null ? userCount : num(c.m365PremiumQty, userCount);
  /* The two mutually exclusive Business Premium plans. The `premium` rates are
     the ones that have been live since the port — 39.48/32.90 is what produced
     the "$395/yr" figure on the page (5 × 6.58 × 12 = 394.8), so they are
     reused here rather than re-supplied. Exactly ONE plan is ever charged,
     shown, or counted toward the annual saving. */
  const plan365 = M365_PLANS[c.m365PlanChoice] ? c.m365PlanChoice : "premium";
  const m365Plan = M365_PLANS[plan365];
  const m365PremiumRate  = m365Plan.monthly;
  const m365AnnRate      = m365Plan.annual;
  const m365StandardRate = 22.44;
  const m365PremiumMonthly  = m365Qty * m365PremiumRate;
  /* The selected plan's own saving. Kept as its own figure — it is the largest
     component of the aggregate below and the one number that was on the page
     before, so a change to it stays visible in the tests. */
  const m365AnnualSaving    = Math.round(m365Qty * (m365PremiumRate - m365AnnRate) * 12);
  const m365StandardMonthly = userCount * m365StandardRate;

  /* Freeform extra M365 licences — an Exchange Online plan, a standalone Teams
     room, whatever the customer actually has. Same shape as additionalRows but
     inside the M365 section, because they are subscriptions and belong under
     that heading rather than under "Additional Monthly Charges". */
  const additionalM365Items = Array.isArray(c.additionalM365Items) ? c.additionalM365Items : [];
  const additionalM365Total = additionalM365Items.reduce(
    (sum, r) => sum + (parseFloat(r.quantity) || 0) * (parseFloat(r.monthlyUnitPrice) || 0), 0);

  /* Aggregate annual saving across the WHOLE M365 section.
     An item contributes only when BOTH a monthly and an annual rate are real
     numbers. A freeform line with no annual price is not an error and is not
     guessed at — it simply is not part of the comparison, because we have no
     annual price to claim a saving against. */
  const annualSavingFor = (monthly, annual, qty) => {
    const m = parseFloat(monthly), a = parseFloat(annual), q = parseFloat(qty) || 0;
    if (!Number.isFinite(m) || !Number.isFinite(a)) return 0;
    /* An annual rate ABOVE the monthly one is not a real negative saving — it is
       the YEARLY total typed into a field that wants the per-month rate under
       annual commitment ($96 where $8.00 belonged). Left to compute it produced
       −$3,096 against a true +$72, and because the box only prints above zero
       the damage was silent: either the box vanished, or worse, it stayed and
       quietly understated the headline figure by netting off against the real
       saving on Business Premium. Excluded, exactly like a blank annual rate —
       the same "leave it out rather than guess" rule. */
    if (a > m) return 0;
    return (m - a) * q * 12;
  };
  const m365TotalAnnualSaving = Math.round(
    annualSavingFor(m365PremiumRate, m365AnnRate, m365Qty)
    + additionalM365Items.reduce(
        (sum, r) => sum + annualSavingFor(r.monthlyUnitPrice, r.annualUnitPrice, r.quantity), 0));

  /* 3CX Phone System — an optional section, priced by hand per client rather
     than from a rate card. Unticked it contributes NOTHING: the section leaves
     the document entirely rather than printing zero-valued lines, so an amount
     typed and then excluded cannot quietly sit in the total. */
  const include3CX = !!c.include3CX;
  const threeCXHostingFee    = num(c.threeCXHostingFee, 0);
  const threeCXSipTrunkPrice = num(c.threeCXSipTrunkPrice, 0);
  const threeCXTotal = include3CX ? threeCXHostingFee + threeCXSipTrunkPrice : 0;

  const additionalRows  = Array.isArray(c.additionalRows) ? c.additionalRows : [];
  const additionalTotal = additionalRows.reduce(
    (sum, r) => sum + (parseFloat(r.qty) || 0) * (parseFloat(r.unitCost) || 0), 0);

  const mDev = deviceCount * perDevRate;
  const mUsr = userCount * perUsrRate;
  const mTot = mDev + mUsr + copperCost + bronzeAddOnCost
             + networkCost + backupCost + spamThreatCost
             + emailSignatureCost + m365PremiumMonthly + additionalM365Total
             + threeCXTotal + additionalTotal;
  const intunePerDevCost = deviceCount * intunePerDevRate;
  // Waiver covers the two fixed setup fees only — the per-device Intune
  // enrolment line is separate work and is still charged.
  const waiveSetupFees   = !!c.waiveSetupFees;
  const onboardingCharged  = waiveSetupFees ? 0 : onboarding;
  const intuneSetupCharged = waiveSetupFees ? 0 : intuneSetup;
  const oTot = onboardingCharged + intunePerDevCost + intuneSetupCharged;

  return {
    deviceCount, userCount, perDevRate, perUsrRate, plan, isHighTier,
    networkRate, networkDeviceCount, networkCost, backupRate, backupCost,
    spamThreatRate, spamThreatCost, emailSignatureRate, emailSignatureCost,
    m365Qty, m365PremiumRate, m365AnnRate, m365StandardRate,
    m365Plan, m365PlanChoice: plan365,
    m365PremiumMonthly, m365AnnualSaving, m365StandardMonthly,
    m365NetUpgrade: (m365PremiumRate - m365StandardRate) * m365Qty,
    additionalM365Items, additionalM365Total, m365TotalAnnualSaving,
    include3CX, threeCXHostingFee, threeCXSipTrunkPrice, threeCXTotal,
    threeCXSipTrunkDescription: c.threeCXSipTrunkDescription || "",
    copperRate, copperDeviceCount, copperCost,
    isBronzeTier, bronzeDeviceCount, bronzeAddOnCost,
    additionalTotal, monthlyDeviceCost: mDev, monthlyUserCost: mUsr,
    monthlyTotal: mTot,
    onboarding, intunePerDevRate, intunePerDevCost, intuneSetup, onceOffTotal: oTot,
    waiveSetupFees, onboardingCharged, intuneSetupCharged,
  };
}

function num(v, dflt) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : dflt;
}
const fmt    = (n) => "$" + Number(n).toLocaleString("en-AU");
const fmtMth = (n) => "$" + Number(n).toLocaleString("en-AU") + "/mth";
// One-off amounts are shown to the cent. toLocaleString, not toFixed:
// toFixed drops the thousands separator ("$2104.00").
const fmtC   = (n) => "$" + Number(n || 0).toLocaleString("en-AU",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2   = fmtC;   // same thing; kept for the M365 call sites

/* ══════════════════════════════════════════════════════════════════════
   Print CSS — VERBATIM from REFINEMENTS.md §4 (full working block) plus
   the §5 .no-print / .print-only helpers and the screen-side page frame.
   The sc-for/sc-if rule is inert in React but is kept because the block is
   copied as-is; removing lines from a battle-tested print block invites
   the exact regressions REFINEMENTS.md documents.
   ══════════════════════════════════════════════════════════════════════ */
export function getProposalPrintCss() {
  return `
*, *::before, *::after { box-sizing: border-box; }
.print-only { display: none; }
body { margin: 0; padding: 0; background: #b8b8b8;
       font-family: 'Inter', Arial, Helvetica, sans-serif;
       text-rendering: optimizeLegibility; font-weight: 500;
       -webkit-font-smoothing: subpixel-antialiased; }
.doc-pages { display: flex; flex-direction: column; align-items: center; gap: 20px; position: relative; }
.page { width: 794px; min-height: 1123px; background: #ffffff; position: relative; overflow: hidden; }

@page { size: A4; margin: 0; }
@media print {
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { background: none !important; padding: 0 !important; margin: 0 !important; width: 210mm !important; }
  html, body { transform: none !important; height: auto !important; overflow: visible !important; }
  body > div, body > div > div, body > div > div > div { transform: none !important; height: auto !important; max-height: none !important; overflow: visible !important; }
  sc-for, sc-if { display: contents !important; }
  #dc-root, .sc-host { height: auto !important; width: 210mm !important; max-width: 210mm !important; overflow: visible !important; transform: none !important; }
  #dc-root > *, .sc-host > * { transform: none !important; max-height: none !important; overflow: visible !important; }
  .doc-pages { display: block !important; gap: 0 !important; width: 210mm !important; padding: 0 !important; margin: 0 !important; }
  .page { box-shadow: none !important; break-after: always !important; page-break-after: always !important; width: 210mm !important; height: 297mm !important; min-height: 0 !important; overflow: hidden !important; border-radius: 0 !important; }
  .page:last-child { break-after: avoid !important; page-break-after: avoid !important; }
  .page[data-print-last] { break-after: avoid !important; page-break-after: avoid !important; }
  .screen-only, #__dtp_panel, #__dtp_bar, .no-print { display: none !important; }
  .print-only { display: inline !important; }
  .hide-in-print { display: none !important; }
}
`.trim();
}

/* ══════════════════════════════════════════════════════════════════════
   Table-layout primitives (Prince has no CSS grid)
   ══════════════════════════════════════════════════════════════════════ */

/* A full-width two-cell row: content left, value right. Replaces the
   source's `display:flex; justify-content:space-between`. */
function SplitRow({ left, right, style, align = "center", rightStyle }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", ...style }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: align === "top" ? "top" : "middle", padding: 0 }}>{left}</td>
          <td style={{ verticalAlign: align === "top" ? "top" : "middle", padding: 0,
                       textAlign: "right", whiteSpace: "nowrap", width: "1%", ...rightStyle }}>
            {right}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/* Replaces `display:grid; grid-template-columns:1fr 1fr; gap:<row> <col>`.
   Column gap comes from cell padding so the outer edges stay flush. */
function TwoColTable({ items, renderItem, colGap = 10, rowGap = 10, style }) {
  const rows = [];
  for (let i = 0; i < items.length; i += 2) rows.push([items[i], items[i + 1]]);
  const half = colGap / 2;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", ...style }}>
      <tbody>
        {rows.map((pair, r) => (
          <tr key={r}>
            {pair.map((item, ci) => (
              <td key={ci}
                  style={{ width: "50%", verticalAlign: "top", padding: 0,
                           paddingLeft:  ci === 0 ? 0 : half,
                           paddingRight: ci === 0 ? half : 0,
                           paddingBottom: r === rows.length - 1 ? 0 : rowGap }}>
                {item ? renderItem(item, r * 2 + ci) : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* Icon/bullet + text, as a two-cell row rather than a flex row. */
function BulletRow({ bullet, children, gap = 9, bottom = 0, bulletWidth }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: bottom }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: "top", padding: 0, width: bulletWidth || 1,
                       paddingRight: gap, whiteSpace: "nowrap" }}>
            {bullet}
          </td>
          <td style={{ verticalAlign: "top", padding: 0 }}>{children}</td>
        </tr>
      </tbody>
    </table>
  );
}

const dot = (color = PROPOSAL_COLORS.teal, size = 5, top = 6) => (
  <div style={{ width: size, height: size, borderRadius: "50%", background: color, marginTop: top }} />
);

/* Raw SVG inner-markup from the source's icon arrays. */
function Icon({ paths, size = 14, stroke = PROPOSAL_COLORS.teal, width = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
         strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
         dangerouslySetInnerHTML={{ __html: paths }} />
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Editable field primitives
   edit  → input/textarea styled to inherit the surrounding typography
   view  → plain text
   print → plain text, no interactive chrome (REFINEMENTS.md §5 pattern B)
   ══════════════════════════════════════════════════════════════════════ */
const inheritInput = {
  background: "rgba(11,128,165,0.06)",
  border: "1px dashed rgba(11,128,165,0.35)",
  borderRadius: 3,
  outline: "none",
  width: "100%",
  padding: "1px 4px",
  margin: 0,
  display: "block",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  fontStyle: "inherit",
  color: "inherit",
  letterSpacing: "inherit",
  lineHeight: "inherit",
  textAlign: "inherit",
};

/* An input that hugs its own text.

   Only ever mounts in EDIT mode — F returns a plain <span> for view/print — so
   the DOM measurement here can never reach the DocRaptor render path. That
   matters: renderToString produces a static string with no layout pass, so
   anything measured at runtime would silently not apply to the PDF.

   Measures a hidden span carrying the same typography, then sets the input to
   that width. Without it the box sits at a fixed character count and trailing
   punctuation floats away from the name ("Hi Jai      ,"). */
function AutoWidthInput({ value, onChange, placeholder, style, className }) {
  const measureRef = React.useRef(null);
  const [textWidth, setTextWidth] = React.useState(null);

  React.useLayoutEffect(() => {
    const el = measureRef.current;
    if (el) setTextWidth(Math.ceil(el.getBoundingClientRect().width));
  }, [value, placeholder, style.fontSize, style.fontWeight, style.letterSpacing]);

  const PAD = 5;   // 2px padding each side + border; keeps punctuation tight

  return (
    <>
      <span ref={measureRef} aria-hidden="true"
            style={{ ...style, position: "absolute", top: -9999, left: -9999,
                     visibility: "hidden", whiteSpace: "pre", pointerEvents: "none",
                     border: 0, padding: 0, width: "auto" }}>
        {value || placeholder || ""}
      </span>
      <input
        type="text" className={className} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...style,
                 width: textWidth == null ? "auto" : textWidth + PAD,
                 padding: "1px 2px", display: "inline-block", verticalAlign: "baseline" }}
      />
    </>
  );
}

/* Inline text field. `style` is the typography of the text it replaces.
   `inline` keeps the input in the surrounding text flow — for a value sitting
   mid-sentence, like the "Hi <name>," salutation. Without it the input is a
   full-width block and the sentence breaks across three lines. */
function F({ mode, value, onChange, style, placeholder, multiline, rows = 3, inline }) {
  if (mode !== "edit") {
    return <span style={style}>{value}</span>;
  }
  if (multiline) {
    return (
      <textarea
        className="no-print"
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inheritInput, ...style, resize: "vertical" }}
      />
    );
  }
  if (inline) {
    return (
      <AutoWidthInput className="no-print" value={value} placeholder={placeholder}
                      onChange={onChange} style={{ ...inheritInput, ...style }} />
    );
  }

  return (
    <input
      type="text"
      className="no-print"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      // Caller style spread LAST: inheritInput is a default, not an override.
      // It previously won, silently discarding display/width from the call site.
      style={{ ...inheritInput, ...style }}
    />
  );
}

/* Number cell that becomes a bold plain value in print — REFINEMENTS.md §5
   pattern B, kept exactly (input.no-print beside span.print-only). */
function NumberCell({ mode, value, onChange, min = 1, max = 500 }) {
  if (mode === "print") {
    return <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{value}</span>;
  }
  const disabled = mode === "view";
  return (
    <>
      <input
        type="number" min={min} max={max} value={value} disabled={disabled}
        onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || min))}
        className="no-print"
        style={{ width: 52, padding: "3px 6px", border: "1px solid #e5e7eb", borderRadius: 4,
                 fontSize: 12, textAlign: "center", fontFamily: "inherit", color: "#1a1a1a" }}
      />
      <span className="print-only" style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{value}</span>
    </>
  );
}

/* Tick box matching the source's 20px square checkbox. */
function CheckBox({ checked, onToggle, mode }) {
  if (mode === "print") return null;
  const common = {
    width: 20, height: 20, borderRadius: 4, cursor: mode === "edit" ? "pointer" : "default",
    display: "inline-block", verticalAlign: "middle",
  };
  const handler = mode === "edit" ? onToggle : undefined;
  if (!checked) {
    return <div className="no-print" onClick={handler}
                style={{ ...common, border: "2px solid #d1d5db", background: "#ffffff" }} />;
  }
  return (
    <div className="no-print" onClick={handler}
         style={{ ...common, border: "2px solid #0b80a5", background: "#0b80a5",
                  textAlign: "center", lineHeight: "16px" }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff"
           strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}

/* Small "+ Add" / "✕" controls, hidden in print. */
function AddButton({ label, onClick, disabled }) {
  return (
    <div className="no-print" onClick={disabled ? undefined : onClick}
         style={{ marginTop: 10, padding: "7px 0", textAlign: "center", cursor: disabled ? "default" : "pointer",
                  background: disabled ? "rgba(11,128,165,0.04)" : "rgba(11,128,165,0.1)",
                  border: "1px dashed rgba(11,128,165,0.3)", borderRadius: 6,
                  fontSize: 10, fontWeight: 700, color: PROPOSAL_COLORS.teal,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  opacity: disabled ? 0.45 : 1 }}>
      {label}
    </div>
  );
}

function DeleteButton({ onClick }) {
  return (
    <div className="no-print" onClick={onClick}
         style={{ width: 20, height: 20, textAlign: "center", lineHeight: "20px",
                  cursor: "pointer", background: "rgba(239,68,68,0.1)", borderRadius: 4 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
           strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Page chrome — header bar, ghost number, footer
   ══════════════════════════════════════════════════════════════════════ */
const PAGE_BASE = {
  width: 794, minHeight: 1123, background: "#ffffff",
  position: "relative", overflow: "hidden",
};

function HeaderBar({ customerName }) {
  return (
    <div style={{ height: 48, background: "#1a1a1a", padding: "0 64px" }}>
      <table style={{ width: "100%", height: 48, borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", padding: 0 }}>
              <span style={{ color: "#0b80a5", fontSize: 10, fontWeight: 700,
                             letterSpacing: "0.18em", textTransform: "uppercase" }}>
                DATATECH SOLUTIONS
              </span>
            </td>
            <td style={{ verticalAlign: "middle", padding: 0, textAlign: "right" }}>
              <span style={{ color: "rgba(255,255,255,0.38)", fontSize: 10,
                             letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {customerName} · Technology Proposal
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function GhostNumber({ n }) {
  return (
    <div style={{ position: "absolute", top: 44, right: 58, fontSize: 160, fontWeight: 900,
                  color: "rgba(0,0,0,0.028)", lineHeight: 1, userSelect: "none",
                  pointerEvents: "none", letterSpacing: "-0.05em" }}>
      {n}
    </div>
  );
}

/* REFINEMENTS.md "Footer anchoring": absolute, direct child of .page,
   white background, z-index 2 so long content can't overlap it. */
function PageFooter() {
  return (
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 36,
                  borderTop: "1px solid #f0f0f0", background: "#ffffff",
                  padding: "0 64px", zIndex: 2 }}>
      <table style={{ width: "100%", height: 36, borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", padding: 0 }}>
              <span style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "0.04em" }}>
                Confidential — Not for Distribution
              </span>
            </td>
            <td style={{ verticalAlign: "middle", padding: 0, textAlign: "right" }}>
              <span style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "0.04em" }}>
                datatech.net.au
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SectionLabel({ text }) {
  return (
    <table style={{ borderCollapse: "collapse", marginBottom: 8 }}>
      <tbody>
        <tr>
          <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 12 }}>
            <div style={{ width: 30, height: 2, background: "#0b80a5" }} />
          </td>
          <td style={{ verticalAlign: "middle", padding: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#0b80a5",
                           letterSpacing: "0.16em", textTransform: "uppercase" }}>
              {text}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 1 — Cover
   Vertical rhythm via a full-height 3-row table (top logo / centred title /
   bottom metadata) instead of the source's flex column: same visual result,
   and table vertical-align is the one thing Prince is reliably good at.
   ══════════════════════════════════════════════════════════════════════ */
function CoverPage({ c, mode, set, assetBase, onCoverImage }) {
  const isEdit = mode === "edit";
  return (
    <div className="page" style={{ ...PAGE_BASE, background: "#1a1a1a" }}>
      {c.coverImage ? (
        <img src={c.coverImage} alt=""
             style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                      objectFit: "cover", objectPosition: "center 38%", display: "block" }} />
      ) : mode === "print" ? null : (
        <div className="no-print"
             style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                      border: "2px dashed rgba(255,255,255,0.18)",
                      color: "rgba(255,255,255,0.35)", fontSize: 13, textAlign: "center",
                      paddingTop: 460 }}>
          Drop a customer photo here — or upload via the settings panel
        </div>
      )}

      {/* Tonal + vignette stack, verbatim */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(12,12,12,0.28)", zIndex: 1 }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to right, rgba(10,10,10,0.82) 0%, rgba(10,10,10,0.55) 38%, rgba(10,10,10,0.0) 72%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to top, rgba(10,10,10,0.90) 0%, rgba(10,10,10,0.40) 18%, rgba(10,10,10,0.0) 38%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
                    background: "linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.0) 18%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, zIndex: 3,
                    background: "linear-gradient(90deg, #0b80a5, #2a4365)" }} />

      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2,
                    padding: "56px 68px 52px" }}>
        <table style={{ width: "100%", height: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ height: 1, verticalAlign: "top", padding: 0 }}>
                <img src={`${assetBase}/proposal-assets/logo-transparent.png`} alt="Datatech Solutions"
                     style={{ height: 56, objectFit: "contain", objectPosition: "left center" }} />
              </td>
            </tr>

            <tr>
              <td style={{ verticalAlign: "middle", padding: "56px 0 44px" }}>
                <table style={{ borderCollapse: "collapse", marginBottom: 22 }}>
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14 }}>
                        <div style={{ width: 44, height: 2, background: "#0b80a5" }} />
                      </td>
                      <td style={{ verticalAlign: "middle", padding: 0 }}>
                        <span style={{ color: "#0b80a5", fontSize: 11, fontWeight: 700,
                                       letterSpacing: "0.2em", textTransform: "uppercase" }}>
                          Technology Success Proposal
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* 46px, not the original 62px, and a wider column: real business
                    names ("Holiday Inn Cockburn") clipped mid-word at 62/540.
                    A FIXED size deliberately — per-name scaling would have to be
                    measured in the DOM, and mode="print" is rendered by
                    renderToString with no layout pass, so the PDF would silently
                    disagree with the preview. 600px keeps the text inside the
                    left vignette. Spans the FULL text column (794 - 68 - 68 =
                    658px) rather than the original 540px cap. */}
                <h1 style={{ color: "#ffffff", fontSize: 46, fontWeight: 800, lineHeight: 1.06,
                             margin: "0 0 26px", letterSpacing: "-0.03em", maxWidth: "100%" }}>
                  <F mode={mode} value={c.customerName} onChange={(v) => set({ customerName: v })}
                     style={{ color: "#ffffff", fontSize: 46, fontWeight: 800, lineHeight: 1.06,
                              letterSpacing: "-0.03em" }} />
                </h1>
                <p style={{ color: "#ffffffc8", fontSize: 16, lineHeight: 1.7, maxWidth: 480,
                            margin: 0, fontWeight: 300 }}>
                  A tailored technology strategy designed to strengthen your IT foundation,
                  protect your operations, and support your long-term success.
                </p>
              </td>
            </tr>

            <tr>
              <td style={{ height: 1, verticalAlign: "bottom", padding: 0 }}>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.13)", paddingTop: 32 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      <tr>
                        <td style={{ verticalAlign: "bottom", padding: 0 }}>
                          <p style={coverKicker}>Prepared For</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 700,
                                      margin: "0 0 3px", letterSpacing: "-0.01em" }}>
                            <F mode={mode} value={c.customerFullName}
                               onChange={(v) => set({ customerFullName: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 700 }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 12,
                                      fontWeight: 300, margin: 0, letterSpacing: "0.01em" }}>
                            {c.customerName}
                          </p>
                        </td>
                        <td style={{ verticalAlign: "bottom", padding: 0, textAlign: "center" }}>
                          <p style={coverKicker}>Prepared By</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, margin: 0,
                                      letterSpacing: "-0.01em" }}>
                            <F mode={mode} value={c.salesRepName} onChange={(v) => set({ salesRepName: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, textAlign: "center" }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12,
                                      margin: "4px 0 0", fontWeight: 300 }}>
                            <F mode={mode} value={c.salesRepTitle} onChange={(v) => set({ salesRepTitle: v })}
                               style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 300, textAlign: "center" }} />
                          </p>
                        </td>
                        <td style={{ verticalAlign: "bottom", padding: 0, textAlign: "right" }}>
                          <p style={coverKicker}>Date</p>
                          <p style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                            <F mode={mode} value={c.proposalDate} onChange={(v) => set({ proposalDate: v })}
                               style={{ color: "#ffffff", fontSize: 15, fontWeight: 600, textAlign: "right" }} />
                          </p>
                          <p style={{ color: "rgba(255,255,255,0.38)", fontSize: 11, margin: "4px 0 0" }}>
                            Confidential
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cover uploads never go through `set` — the image must not land in
          `content`, which snapshots deep-clone. onCoverImage routes it to the
          shell's deduplicated store. No handler, no control. */}
      {isEdit && onCoverImage && (
        <div className="no-print"
             style={{ position: "absolute", top: 12, right: 12, zIndex: 5 }}>
          <label style={{ display: "inline-block", padding: "6px 12px", cursor: "pointer",
                          background: "rgba(11,128,165,0.9)", color: "#fff", borderRadius: 6,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                          textTransform: "uppercase" }}>
            {c.coverImage ? "Change cover" : "Upload cover"}
            <input type="file" accept="image/*" style={{ display: "none" }}
                   onChange={(e) => readCoverImage(e, (dataUrl) => onCoverImage(dataUrl))} />
          </label>
          {c.coverImage && (
            <span onClick={() => onCoverImage(null)}
                  style={{ display: "inline-block", marginLeft: 6, padding: "6px 10px",
                           cursor: "pointer", background: "rgba(239,68,68,0.9)", color: "#fff",
                           borderRadius: 6, fontSize: 10, fontWeight: 700,
                           letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Remove
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const coverKicker = {
  color: "rgba(255,255,255,0.38)", fontSize: 10, fontWeight: 600,
  letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 7px",
};

/* Client-side compress to JPEG before storing — SETUP.md "Cover Image
   Upload". Keeps the state blob (and the PDF) a sane size. */
export function readCoverImage(event, done) {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      done(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 2 — Introduction
   ══════════════════════════════════════════════════════════════════════ */
const INTRO_PLACEHOLDERS = [
  "✏️ Paragraph 1 — Open with a personal connection. Reference where you met, thank them for their time, and acknowledge something specific about their business.",
  "✏️ Paragraph 2 — Bridge to the proposal. Explain that everything they shared, combined with your on-site observations, has shaped the recommendations inside.",
  "✏️ Paragraph 3 — Close with confidence. Reinforce your commitment to their success and your excitement about partnering with them.",
];

function IntroPage({ c, mode, set }) {
  const paraStyle = { fontSize: 14.5, lineHeight: 1.75, color: "#1f2937", margin: 0 };
  const keys = ["introPara1", "introPara2", "introPara3"];
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} />
      <GhostNumber n="01" />
      <div style={{ padding: "44px 80px 44px", position: "relative", minHeight: 1123 - 48 - 36 }}>
        <SectionLabel text="Section 01 — Introduction" />
        <p style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", margin: "0 0 36px",
                    letterSpacing: "-0.02em" }}>
          Hi{" "}
          <F mode={mode} value={c.customerContact} onChange={(v) => set({ customerContact: v })}
             inline placeholder="first name"
             style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", letterSpacing: "-0.02em" }} />
          ,
        </p>

        <div>
          {keys.map((k, i) => {
            const val = c[k];
            if (mode !== "edit" && !val) return null;   // never ship the coaching text
            return (
              <div key={k} style={{ marginBottom: 18 }}>
                <F mode={mode} value={val} onChange={(v) => set({ [k]: v })}
                   placeholder={INTRO_PLACEHOLDERS[i]} multiline rows={4} style={paraStyle} />
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 44, paddingTop: 28, borderTop: "1px solid #f0f0f0" }}>
          <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 14px" }}>Best regards,</p>
          <p style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a", margin: "0 0 4px",
                      letterSpacing: "-0.02em" }}>{c.salesRepName}</p>
          <p style={{ fontSize: 13, color: "#4b5563", margin: 0, fontWeight: 500 }}>{c.salesRepTitle}</p>
          <p style={{ fontSize: 13, color: "#0b80a5", margin: "3px 0 0", fontWeight: 600 }}>
            Datatech Solutions
          </p>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGES 3 & 4 — What We Heard / What We've Found
   Same card layout, different accent + icon set.
   ══════════════════════════════════════════════════════════════════════ */
export const CHALLENGE_ICONS = [
  '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 010 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>',
  '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  '<path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01"/>',
  '<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>',
  '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
  '<rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>',
];

export const FINDING_ICONS = [
  '<rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>',
  '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9" y1="12" x2="15" y2="12"/>',
  '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
  '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
  '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
];

function EmptyBlock({ title, hint, pad = 24 }) {
  return (
    <div style={{ border: "2px dashed #e5e7eb", borderRadius: 10, padding: pad,
                  textAlign: "center", color: "#6b7280" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d1d5db"
           strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ margin: "0 auto 8px", display: "block" }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
      <p style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", margin: "0 0 3px" }}>{title}</p>
      <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{hint}</p>
    </div>
  );
}

/* One card in the What We Heard / What We've Found grids. */
function ItemCard({ item, index, mode, icons, accent, iconBg, iconStroke, onPatch, onDelete }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e5e9ef",
                  borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: 16,
                  position: "relative" }}>
      {mode === "edit" && (
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <DeleteButton onClick={onDelete} />
        </div>
      )}
      <div style={{ width: 28, height: 28, background: iconBg, borderRadius: 6,
                    marginBottom: 9, textAlign: "center", lineHeight: "28px" }}>
        <Icon paths={icons[index % icons.length]} stroke={iconStroke} />
      </div>
      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", margin: "0 0 6px",
                   letterSpacing: "-0.01em" }}>
        <F mode={mode} value={item.title} onChange={(v) => onPatch({ title: v })}
           placeholder="Title…"
           style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.01em" }} />
      </h3>
      <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, margin: 0 }}>
        <F mode={mode} value={item.description} onChange={(v) => onPatch({ description: v })}
           placeholder="Describe…" multiline rows={3}
           style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6 }} />
      </p>
    </div>
  );
}

function ListCardPage({ c, mode, set, cfg }) {
  const items = Array.isArray(c[cfg.key]) ? c[cfg.key] : [];
  const patch = (i, p) => set({ [cfg.key]: items.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const remove = (i) => set({ [cfg.key]: items.filter((_, j) => j !== i) });
  const add = () => {
    if (items.length >= MAX_LIST_ITEMS) return;
    set({ [cfg.key]: [...items, { title: "", description: "" }] });
  };

  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} />
      <GhostNumber n={cfg.ghost} />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: 1123 - 48 - 36 }}>
        <SectionLabel text={cfg.section} />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>{cfg.heading}</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
          {cfg.subtitle(c.customerName)}
        </p>

        {items.length === 0 ? (
          <EmptyBlock title={cfg.emptyTitle} hint={cfg.emptyHint} />
        ) : (
          <TwoColTable
            items={items}
            renderItem={(item, i) => (
              <ItemCard item={item} index={i} mode={mode} icons={cfg.icons}
                        accent={cfg.accent} iconBg={cfg.iconBg} iconStroke={cfg.iconStroke}
                        onPatch={(p) => patch(i, p)} onDelete={() => remove(i)} />
            )}
          />
        )}

        {mode === "edit" && (
          <AddButton label={`${cfg.addLabel} (${items.length}/${MAX_LIST_ITEMS})`}
                     onClick={add} disabled={items.length >= MAX_LIST_ITEMS} />
        )}
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 5 — Our Recommendation
   ══════════════════════════════════════════════════════════════════════ */
const BRONZE_INCLUDED = [
  "Managed Device Support",
  "Remote Monitoring & Management",
  "Monthly Patch Management",
  "Device Health Monitoring",
  "Proactive Device Maintenance",
  "Ransomware Detection & Response",
  "Endpoint Threat Protection",
  "24/7 SOC Threat Monitoring",
];
const BRONZE_EXCLUDED = [
  "Helpdesk Support",
  "Dark Web Monitoring",
  "M365 Intune & Defender Management",
  "Dedicated Account Manager",
  "Microsoft 365 Maintenance",
  "Phishing Campaign & Training",
];

const UPSELL_POINTS = [
  ["M365 Intune & Defender Mgmt", "Microsoft 365 identity and device policy left unmanaged is an open door."],
  ["Dark Web Monitoring", "stolen credentials trade online long before they're ever used."],
  ["Phishing Training", "staff remain the most common way attackers get in the door."],
  ["Helpdesk & Account Manager", "proactive support when it counts, not billed after the fact."],
];

function RecommendPage({ c, mode, t }) {
  const features = [
    ...BRONZE_INCLUDED.map((label) => ({ label, on: true })),
    ...BRONZE_EXCLUDED.map((label) => ({ label, on: false })),
  ];

  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} />
      <GhostNumber n="04" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: 1123 - 48 - 36 }}>
        <SectionLabel text="Section 04" />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Our Recommendation</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 24px", lineHeight: 1.5 }}>
          Based on our discovery and assessment, we recommend the following managed services
          plan for {c.customerName}.
        </p>

        {/* Featured plan card */}
        <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "26px 40px",
                      position: "relative", overflow: "hidden", marginBottom: 16 }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4,
                        background: "linear-gradient(90deg, #0b80a5, #2a4365)" }} />
          <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220,
                        borderRadius: "50%", background: "rgba(11,128,165,0.07)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -40, right: 60, width: 120, height: 120,
                        borderRadius: "50%", background: "rgba(11,128,165,0.05)", pointerEvents: "none" }} />

          <SplitRow
            align="top"
            style={{ marginBottom: 24, position: "relative" }}
            left={
              <>
                <p style={{ color: "#0b80a5", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
                            textTransform: "uppercase", margin: "0 0 8px" }}>Our Recommended Plan</p>
                <h3 style={{ color: "#ffffff", fontSize: 30, fontWeight: 800, margin: 0,
                             letterSpacing: "-0.02em" }}>{c.recommendedPlan}</h3>
              </>
            }
            right={
              <>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, textTransform: "uppercase",
                            letterSpacing: "0.1em", margin: "0 0 5px" }}>Per Device</p>
                <p style={{ color: "#0b80a5", fontSize: 22, fontWeight: 800, margin: 0,
                            letterSpacing: "-0.02em" }}>
                  {fmt(t.perDevRate)}
                  <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)" }}>/mth</span>
                </p>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: "4px 0 0",
                            fontStyle: "italic" }}>Device-based pricing — no per-user fee</p>
              </>
            }
          />

          <div style={{ height: 1, background: "rgba(255,255,255,0.1)", marginBottom: 22 }} />

          <TwoColTable
            items={features}
            colGap={28}
            rowGap={8}
            style={{ position: "relative" }}
            renderItem={(f) => (
              <BulletRow
                gap={10}
                bullet={
                  <span style={{ color: f.on ? "#0b80a5" : "rgba(255,255,255,0.25)",
                                 fontWeight: 800, fontSize: 13, display: "inline-block", marginTop: 1 }}>
                    {f.on ? "✓" : "—"}
                  </span>
                }
              >
                <span style={{ color: f.on ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.35)",
                               fontSize: 13, lineHeight: 1.4,
                               textDecoration: f.on ? "none" : "line-through",
                               textDecorationColor: "rgba(255,255,255,0.15)" }}>
                  {f.label}
                </span>
              </BulletRow>
            )}
          />
        </div>

        {/* Why we recommend this */}
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8,
                      padding: "16px 22px", marginBottom: 12 }}>
          <BulletRow
            gap={14}
            bulletWidth={32}
            bullet={
              <div style={{ width: 32, height: 32, background: "#0b80a5", borderRadius: 6,
                            marginTop: 1, textAlign: "center", lineHeight: "32px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff"
                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            }
          >
            <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", margin: "0 0 5px" }}>
              Why we recommend this for {c.customerName}
            </p>
            <p style={{ fontSize: 13, color: "#1f2937", lineHeight: 1.65, margin: "0 0 10px" }}>
              {c.recommendedPlan} gives {c.customerName} a strong, proactive foundation — remote
              monitoring, patch management, device health, ransomware detection and endpoint
              protection — at a straightforward per-device price.
            </p>
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6,
                          padding: "9px 12px" }}>
              <BulletRow
                gap={8}
                bullet={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#b45309"
                       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                       style={{ marginTop: 1 }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                }
              >
                <p style={{ fontSize: 12, color: "#b45309", margin: 0, lineHeight: 1.55, fontWeight: 500 }}>
                  Please note: on this plan helpdesk support, changes to network, and project work
                  is charged at our standard hourly rate.
                </p>
              </BulletRow>
            </div>
          </BulletRow>
        </div>

        {/* Gold Fortress upsell */}
        <div style={{ background: "#1a1a1a", border: "1px solid rgba(11,128,165,0.3)",
                      borderRadius: 8, padding: "18px 22px" }}>
          <table style={{ borderCollapse: "collapse", marginBottom: 9 }}>
            <tbody>
              <tr>
                <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 10 }}>
                  <div style={{ width: 28, height: 28, background: "rgba(11,128,165,0.15)",
                                border: "1px solid rgba(11,128,165,0.3)", borderRadius: 6,
                                textAlign: "center", lineHeight: "28px" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0b80a5"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </div>
                </td>
                <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#ffffff", margin: 0 }}>
                    Consider upgrading to Gold Fortress
                  </p>
                </td>
                <td style={{ verticalAlign: "middle", padding: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#0b80a5",
                                 background: "rgba(11,128,165,0.15)", padding: "2px 8px",
                                 borderRadius: 20, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Recommended Upgrade
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.5)", lineHeight: 1.5,
                      margin: "0 0 11px" }}>
            Modern threats don't keep business hours. Here's what {c.recommendedPlan} doesn't
            cover — and why it matters for {c.customerName}:
          </p>
          <TwoColTable
            items={UPSELL_POINTS}
            colGap={20}
            rowGap={9}
            renderItem={([title, body]) => (
              <BulletRow gap={8} bullet={dot("#0b80a5", 5, 5)}>
                <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.5, margin: 0 }}>
                  <span style={{ fontWeight: 700, color: "#ffffff" }}>{title}</span> — {body}
                </p>
              </BulletRow>
            )}
          />
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 6 — Service Tier Comparison (already a real table in the source)
   ══════════════════════════════════════════════════════════════════════ */
/* Columns are [Copper, Bronze, Silver, Gold] — the same left-to-right order as
   TIERS below, so the ticks visibly accumulate across the row (2 / 8 / 9 / 14).

   ⚠ COPPER IS ENDPOINT SECURITY ONLY: Endpoint Threat Protection and Monthly
   Patch Management, nothing else. It carried Managed Device Support and Remote
   Monitoring & Management until 2026-08-29, which advertised the managed-support
   tiers' headline inclusions on a $15/device product that does not deliver them
   — and simultaneously withheld patch management, which it does. Corrected on
   Craig's call. Do not "restore" either from an older source document. */
export const FEATURE_MATRIX = [
  /* ⚠ ORDER IS DELIBERATE, and it is the SECOND half of the Copper fix. With
     the columns running Copper→Gold, leading with Copper's own two features
     makes the table a staircase in both directions: more tiers carry each
     feature as you read RIGHT, and each tier's additions follow the previous
     tier's as you read DOWN. Sorting these alphabetically, or restoring the old
     order, breaks the read even though every tick stays correct. */
  ["Endpoint Threat Protection",      true,  true,  true,  true],
  ["Monthly Patch Management",        true,  true,  true,  true],
  ["Managed Device Support",          false, true,  true,  true],
  ["Remote Monitoring & Management",  false, true,  true,  true],
  ["Device Health Monitoring",        false, true,  true,  true],
  ["Proactive Device Maintenance",    false, true,  true,  true],
  ["Ransomware Detection & Response", false, true,  true,  true],
  ["24/7 SOC Threat Monitoring",      false, true,  true,  true],
  ["Helpdesk Support",                false, false, true,  true],
  ["M365 Intune & Defender Mgmt",     false, false, false, true],
  ["Microsoft 365 Maintenance",       false, false, false, true],
  ["Dedicated Account Manager",       false, false, false, true],
  ["Phishing Campaign & Training",    false, false, false, true],
  /* Added when Silver shipped. Silver's source carried this row and Bronze/Gold
     did not, which would have put two different-length tables in front of the
     same prospect. All three lists are now identical and a test pins them. */
  ["Dark Web Monitoring",             false, false, false, true],
];

const TIERS = [
  { key: "Copper Defender", label: "Copper",        price: "$15/dev",              cellBg: "#fafafa", head: "rgba(255,255,255,0.65)" },
  { key: "Bronze Sentinel", label: "Bronze",        price: "$49/dev",              cellBg: null,      head: "rgba(255,255,255,0.65)" },
  { key: "Silver Guardian", label: "Silver",        price: "$49/dev + $95/user",   cellBg: "#fafafa", head: "rgba(255,255,255,0.65)" },
  { key: "Gold Fortress",   label: "Gold Fortress", price: "$49/dev + $120/user",  cellBg: "#f0f9ff", head: "#0b80a5" },
];

function ComparisonPage({ c }) {
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} />
      <GhostNumber n="05" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: 1123 - 48 - 36 }}>
        <SectionLabel text="Section 05" />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Service Tier Comparison</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 24px", lineHeight: 1.5 }}>
          {/* ⚠ NOT "All plans include device management and remote access" — that
              sentence stood here until 2026-08-29 and, once Copper's row was
              corrected, it contradicted the two dashes printed directly beneath
              it. Copper is endpoint security only; managed support starts at
              Bronze. A standfirst that disagrees with its own table is worse
              than none, and this one sat one line above the evidence. */}
          Bronze and above include device management and remote access. Higher tiers add
          greater security depth and account management.
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "13px 16px", background: "#1a1a1a",
                           color: "rgba(255,255,255,0.6)", fontWeight: 500, fontSize: 11,
                           width: "36%", borderBottom: "3px solid #0b80a5" }}>Feature</th>
              {TIERS.map((tier) => (
                <th key={tier.key}
                    style={{ textAlign: "center", padding: "10px 8px", background: "#1a1a1a", width: "16%" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: tier.head,
                                letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 3 }}>
                    {tier.label}
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>
                    {tier.price}
                  </div>
                  {c.recommendedPlan === tier.key && (
                    <div style={{ background: "#0b80a5", color: "white", fontSize: 8.5,
                                  padding: "2px 6px", borderRadius: 3, marginTop: 5,
                                  display: "inline-block", letterSpacing: "0.06em",
                                  textTransform: "uppercase", fontWeight: 700 }}>
                      Recommended
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_MATRIX.map(([label, ...flags]) => (
              <tr key={label} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "8px 16px", fontSize: 12.5, color: "#1f2937", fontWeight: 500 }}>
                  {label}
                </td>
                {flags.map((on, i) => (
                  <td key={TIERS[i].key}
                      style={{ textAlign: "center", padding: "8px 8px",
                               background: TIERS[i].cellBg || undefined }}>
                    {on
                      ? <span style={{ color: "#0b80a5", fontWeight: 800, fontSize: 14 }}>✓</span>
                      : <span style={{ color: "#e5e7eb", fontSize: 13 }}>—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ fontSize: 11.5, color: "#4b5563", margin: "16px 0 0", fontStyle: "italic" }}>
          All prices are exclusive of GST. Requires Microsoft 365 Premium license for Bronze
          Sentinel, Silver Guardian and Gold Fortress. Optional add-ons available — ask your
          account manager.
        </p>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 7 — Your IT Investment
   REFINEMENTS.md §7: this page is tuned to exactly A4. Vertical rhythm
   values here (30px top pad, 11px row padding, 8px band padding) are the
   tightened ones — do not loosen them.
   ══════════════════════════════════════════════════════════════════════ */
function SectionBand({ title, hint, extraStyle, action }) {
  return (
    <div style={{ padding: action ? "6px 24px" : "7px 24px", background: "#1f2937",
                  borderLeft: "4px solid #0b80a5", ...extraStyle }}>
      <SplitRow
        left={
          <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em", color: "#ffffff",
                      textTransform: "uppercase", margin: 0 }}>
            {title}
            {hint && <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 600 }}> {hint}</span>}
          </p>
        }
        right={action || null}
      />
    </div>
  );
}

/* Row heights derived from this page's own markup — 8px vertical padding plus
   the line boxes inside. Rounded up. Verified against live DOM measurement.   */
/* MEASURED from the live page, not estimated — this page runs at ~99% of A4
   with default content, so guessed values split it spuriously. Card segments
   total 853px; with the 94px intro and the 58px closing note that is 1005 of
   roughly 1020 usable. Re-measure before changing any row's padding. */
const IH = {
  band:        29,   // SectionBand, no action button (27-29 by title length)
  bandAction:  37,   // ...with the Add Row control
  device:      51,
  user:        51,   // same two-line shape as the device row
  copper:      59,   // toggle row: title, count + rate line
  copperEdit:  63,   // ...with the rate as an input, which is taller
  bronzeAddOn: 59,   // ...and the same shape again
  m365premium: 40,   // title + qty line (the savings pill moved to its own box)
  m365plan:    62,   // two radio options, editor only
  m365ItemEdit:44,   // one row of inputs
  m365ItemEditWarn:66, // ...plus the wrong-rate explanation
  m365ItemPrint:40,  // description + the qty/rate line beneath it
  m365Saving:  50,
  threeCXRow:  40,   // title + the amount beside it
  threeCXRowEdit:44, // ...with inputs, which are taller   // aggregate annual-commitment box
  m365standard:50,
  networkCovered: 34, // "Devices covered" heading + one line; grows with the text
  network:     59,
  backup:      52,
  spam:        68,   // title, rate line, and the "Replaces traditional..." note
  signature:   52,
  addlHead:    22,
  addlRowEdit: 36,   // inputs are taller than printed text
  addlRowPrint:23,
  spacer:      14,
  totalBar:    42,
  onceGap:     10,   // breathing room above the One-Off Costs band
  onceRow:     52,
  onceRowBadge:78,   // ...plus the "Fee waived" pill
  notes:       58,
};

/* The devices-covered block grows with what is typed into it, so a long list
   cannot silently overrun the page the way a fixed estimate would. ~78 chars to
   a 12px line across the card's inner width, and explicit newlines are honoured
   because the printed text is `pre-wrap`. The editor's textarea is a fixed two
   rows regardless. */
export function networkCoveredH(text, interactive) {
  const HEAD = 16, LINE = 18, PAD = 9;
  if (interactive) return HEAD + PAD + 44;
  const lines = String(text || "").split("\n")
    .reduce((n, ln) => n + Math.max(1, Math.ceil(ln.length / 78)), 0);
  return HEAD + PAD + Math.max(1, lines) * LINE;
}

/* Budget for this page specifically. Its padding is 30/48, not the 44/52 the
   generic constants assume, and the percentage trim used elsewhere is far too
   coarse here: at 99% occupancy a 4% trim splits the default page. Default
   content needs 1005, one extra charge row pushes it to 1028 — so a budget in
   between splits exactly when it should and not before. */
const INVESTMENT_INTRO_H = 94;
/* Real usable height: 1123 page - 48 header - 30 top pad - 48 bottom pad
   - 14 card margin = 983. Measured, then confirmed against a page that
   overflowed to 1149 while an over-generous 1014 budget thought it fit. */
const INVESTMENT_CAPACITY = 980;

function InvestmentPage({ c, mode, set, t }) {
  const rows = Array.isArray(c.additionalRows) ? c.additionalRows : [];
  const patchRow = (i, p) => set({ additionalRows: rows.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const delRow = (i) => set({ additionalRows: rows.filter((_, j) => j !== i) });
  const addRow = () => set({ additionalRows: [...rows, { description: "", qty: "1", unitCost: "" }] });
  const interactive = mode === "edit";

  /* Extra Microsoft 365 licences. `annualUnitPrice` is deliberately absent from
     the seeded row rather than "" — most entries never have one, and it only
     matters to the annual-saving aggregate. */
  const m365Items = Array.isArray(c.additionalM365Items) ? c.additionalM365Items : [];
  const patchM365Item = (i, p) =>
    set({ additionalM365Items: m365Items.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const delM365Item = (i) => set({ additionalM365Items: m365Items.filter((_, j) => j !== i) });
  const addM365Item = () =>
    set({ additionalM365Items: [...m365Items, { description: "", quantity: "1", monthlyUnitPrice: "" }] });

  /* The card is built as a list of height-tagged segments so it can be split
     across pages. `keepWithNext` binds a band heading to the rows beneath it —
     a "One-Off Costs" bar alone at the foot of a page would read as a fault. */
  const seg = [];
  const push = (key, h, node, keepWithNext = false) => seg.push({ key, h, node, keepWithNext });

  push("band-monthly", IH.band, <SectionBand key="bm" title="Managed Services — Monthly" />, true);
  push("device", IH.device, (
    <div key="dev" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
              {c.recommendedPlan} — Devices
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.deviceCount} devices × {fmt(t.perDevRate)}/mth
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
            {fmt(t.monthlyDeviceCost)}/mth
          </p>
        }
      />
    </div>
  ));

  /* The per-user line. Guarded on the RATE, not on the tier: `monthlyUserCost`
     is always in `monthlyTotal`, so a tier that charges per user and does not
     print this row shows a total the customer cannot reconcile from the lines
     above it. Bronze seeds perUserRate 0 and so renders nothing here today —
     but the rate is an editable per-proposal field, and without this the moment
     anyone sets it the charge lands in the total with no line to explain it.
     That is exactly how Gold hid $960/mth. Identical in all three templates. */
  if (t.perUsrRate > 0) push("user", IH.user, (
    <div key="usr" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
              {c.recommendedPlan} — Users
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.userCount} users × {fmt(t.perUsrRate)}/mth
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
            {fmt(t.monthlyUserCost)}/mth
          </p>
        }
      />
    </div>
  ));

  push("band-m365", interactive ? IH.bandAction : IH.band,
    <SectionBand key="bm365" title="Microsoft 365 Subscription"
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                 action={interactive ? (
                   <div onClick={addM365Item} className="no-print"
                        style={{ display: "inline-block", cursor: "pointer", padding: "4px 11px",
                                 background: "#0b80a5", borderRadius: 4 }}>
                     <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                    letterSpacing: "0.06em", textTransform: "uppercase" }}>
                       + Add Licence
                     </span>
                   </div>
                 ) : null} />, true);

  /* Plan choice. Editor-only: the finished document shows ONLY the selected
     plan, because a proposal listing both reads as a comparison the customer is
     being asked to resolve, and only one of them can ever be in the total. */
  if (interactive) push("m365-plan", IH.m365plan, (
    <div key="m365pick" className="no-print"
         style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6", background: "#fbfdfe" }}>
      {Object.values(M365_PLANS).map((p) => (
        <div key={p.key} onClick={() => set({ m365PlanChoice: p.key })}
             style={{ display: "block", cursor: "pointer", padding: "3px 0" }}>
          <table style={{ borderCollapse: "collapse" }}><tbody><tr>
            <td style={{ padding: 0, paddingRight: 10, width: 16, verticalAlign: "middle" }}>
              <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: "50%",
                             border: `1px solid ${t.m365PlanChoice === p.key ? "#0b80a5" : "#cbd5e1"}`,
                             background: t.m365PlanChoice === p.key ? "#0b80a5" : "#ffffff",
                             boxShadow: t.m365PlanChoice === p.key ? "inset 0 0 0 2.5px #ffffff" : "none" }} />
            </td>
            <td style={{ padding: 0, verticalAlign: "middle" }}>
              <span style={{ fontSize: 12.5,
                             fontWeight: t.m365PlanChoice === p.key ? 700 : 500,
                             color: t.m365PlanChoice === p.key ? "#1a1a1a" : "#6b7280" }}>
                {p.label}
              </span>
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                {fmt2(p.monthly)}/user/mth · {fmt2(p.annual)} annualised
              </span>
            </td>
          </tr></tbody></table>
        </div>
      ))}
    </div>
  ), true);

  push("m365-premium", IH.m365premium, (
    <div key="m365p" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", marginRight: 10 }}>
              {t.m365Plan.label}
            </span>
            <span style={{ whiteSpace: "nowrap" }}>
              <NumberCell mode={mode} value={t.m365Qty}
                          onChange={(v) => set({ m365PremiumQty: v })} />
              <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>
                users × {fmt2(t.m365PremiumRate)}/user/mth
              </span>
            </span>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                      whiteSpace: "nowrap" }}>{fmt2(t.m365PremiumMonthly)}/mth</p>
        }
      />
    </div>
  ));

  /* Pushed only when it will actually appear: a `hide-in-print` row still
     consumed its 50px of page budget, spending height on nothing. */
  if (c.showM365Standard || interactive) push("m365-standard", IH.m365standard, (
    <div key="m365s" className={c.showM365Standard ? "" : "hide-in-print"}
         style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb", background: "#fafafa" }}>
      <SplitRow
        left={
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                {mode !== "print" && (
                  <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14, width: 20 }}>
                    <CheckBox mode={mode} checked={!!c.showM365Standard}
                              onToggle={() => set({ showM365Standard: !c.showM365Standard })} />
                  </td>
                )}
                <td style={{ verticalAlign: "middle", padding: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: "#4b5563", margin: "0 0 2px" }}>
                    Microsoft 365 Business Standard{" "}
                    <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 500, fontStyle: "italic" }}>
                      — current plan, not added to total
                    </span>
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                    {t.userCount} users × $22.44/user/mth = {fmt2(t.m365StandardMonthly)}/mth
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        }
        right={
          <span style={{ fontSize: 11, color: "#0b80a5", fontWeight: 600, background: "#eff6ff",
                         border: "1px solid #bfdbfe", padding: "3px 10px", borderRadius: 20,
                         whiteSpace: "nowrap" }}>
            Net upgrade cost: +{fmt2(t.m365NetUpgrade)}/mth
          </span>
        }
      />
    </div>
  ));

  /* Freeform extra licences. Each row is its own segment so a long list flows
     across pages rather than forcing the card onto one, exactly like the
     Additional Monthly Charges list below. */
  m365Items.forEach((r, i) => {
    const qty = parseFloat(r.quantity) || 0;
    const rowTotal = qty * (parseFloat(r.monthlyUnitPrice) || 0);
    const hasAnnual = Number.isFinite(parseFloat(r.annualUnitPrice));
    const suspect = m365ItemRateSuspect(r);
    push(`m365item-${i}`,
         interactive ? (suspect ? IH.m365ItemEditWarn : IH.m365ItemEdit) : IH.m365ItemPrint, (
      <div key={`m365i${i}`} style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
        <SplitRow
          left={interactive ? (
            <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody><tr>
              <td style={{ padding: "0 8px 0 0" }}>
                <input type="text" value={r.description || ""}
                       placeholder="e.g. Microsoft 365 Online Exchange Plan 1"
                       onChange={(e) => patchM365Item(i, { description: e.target.value })}
                       style={addlInput} />
              </td>
              <td style={{ padding: "0 8px 0 0", width: 58 }}>
                <input type="number" min="0" value={r.quantity || ""} placeholder="Qty"
                       onChange={(e) => patchM365Item(i, { quantity: e.target.value })}
                       style={{ ...addlInput, textAlign: "center" }} />
              </td>
              <td style={{ padding: "0 8px 0 0", width: 90 }}>
                <MoneyInput value={r.monthlyUnitPrice} ariaLabel="Monthly unit price"
                            inputStyle={addlInput}
                            onCommit={(v) => patchM365Item(i, { monthlyUnitPrice: v })} />
              </td>
              <td style={{ padding: 0, width: 90 }}>
                {/* Optional. Left blank the item simply does not take part in the
                    annual-saving comparison — there is no annual price to claim
                    a saving against, and inventing one would overstate it. */}
                <MoneyInput value={r.annualUnitPrice} ariaLabel="Annual-commitment unit price"
                            inputStyle={addlInput} invalid={suspect}
                            onCommit={(v) => patchM365Item(i, { annualUnitPrice: v })} />
              </td>
              <td style={{ padding: "0 0 0 8px", width: 28 }}>
                <div className="no-print" onClick={() => delM365Item(i)}
                     style={{ width: 24, height: 24, textAlign: "center", lineHeight: "24px",
                              cursor: "pointer", borderRadius: 4, background: "#fef2f2" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
              </td>
            </tr>
            {suspect && (
              /* Editor-only. The row still bills normally; it is only left out
                 of the saving. Said out loud because a silent exclusion is how
                 the wrong number reaches a customer. */
              <tr><td colSpan={5} style={{ padding: "4px 0 0" }}>
                <span style={{ fontSize: 10.5, color: "#b91c1c" }}>
                  Annual rate is higher than the monthly rate, so this line is left out of the
                  saving. Enter the per-month rate under annual commitment, not the yearly total.
                </span>
              </td></tr>
            )}
            </tbody></table>
          ) : (
            <>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
                {r.description}
              </p>
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                {qty} × {fmt2(parseFloat(r.monthlyUnitPrice) || 0)}/mth
                {hasAnnual && !suspect
                  ? ` · ${fmt2(parseFloat(r.annualUnitPrice))}/mth on annual commitment` : ""}
              </p>
            </>
          )}
          right={interactive ? null : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(rowTotal)}/mth</p>
          )}
        />
      </div>
    ));
  });

  /* The annual-commitment saving, for the WHOLE section rather than per line.
     It used to sit beside Business Premium alone, which understated it the
     moment anything else in the section had an annual rate. Suppressed at zero:
     "Save $0/yr" is not an offer. */
  if (t.m365TotalAnnualSaving > 0) push("m365-saving", IH.m365Saving, (
    <div key="m365save" style={{ padding: "9px 24px", borderBottom: "1px solid #e5e7eb",
                                 background: "#f0fdf4" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#059669", margin: "0 0 2px" }}>
              Save {fmt(t.m365TotalAnnualSaving)}/yr switching to annual commitment
            </p>
            <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>
              Across the Microsoft 365 subscriptions above, compared with month-to-month pricing.
            </p>
          </>
        }
        right={null}
      />
    </div>
  ));

  /* 3CX Phone System. Its own section between Microsoft 365 and the add-ons.
     Excluded, the whole thing LEAVES the document — it is not merely hidden and
     it contributes nothing to the total, so an amount typed and then unticked
     cannot linger in a figure the customer is asked to reconcile. It is still
     shown while editing, unticked, or there would be no way to turn it on. */
  if (t.include3CX || interactive) {
    push("band-3cx", interactive ? IH.bandAction : IH.band, (
      <SectionBand key="b3cx" title="3CX Phone System"
                   hint={interactive && !t.include3CX ? "— not included" : undefined}
                   extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                   action={interactive ? (
                     <span className="no-print" onClick={() => set({ include3CX: !c.include3CX })}
                           style={{ cursor: "pointer", display: "inline-block" }}>
                       <table style={{ borderCollapse: "collapse" }}><tbody><tr>
                         <td style={{ padding: 0, paddingRight: 8, verticalAlign: "middle" }}>
                           <CheckBox mode={mode} checked={t.include3CX}
                                     onToggle={() => set({ include3CX: !c.include3CX })} />
                         </td>
                         <td style={{ padding: 0, verticalAlign: "middle" }}>
                           <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                          letterSpacing: "0.06em", textTransform: "uppercase" }}>
                             Include
                           </span>
                         </td>
                       </tr></tbody></table>
                     </span>
                   ) : null} />
    ), true);

    push("3cx-hosting", interactive ? IH.threeCXRowEdit : IH.threeCXRow, (
      <div key="3cxh" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6",
                               opacity: t.include3CX ? 1 : 0.45 }}>
        <SplitRow
          left={
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
              Monthly Hosting &amp; Management
            </p>
          }
          right={interactive ? (
            <span style={{ display: "inline-block", width: 104 }}>
              <MoneyInput value={c.threeCXHostingFee} ariaLabel="3CX monthly hosting fee"
                          inputStyle={addlInput}
                          onCommit={(v) => set({ threeCXHostingFee: v })} />
            </span>
          ) : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(t.threeCXHostingFee)}/mth</p>
          )}
        />
      </div>
    ));

    push("3cx-sip", interactive ? IH.threeCXRowEdit : IH.threeCXRow, (
      <div key="3cxs" style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb",
                               opacity: t.include3CX ? 1 : 0.45 }}>
        <SplitRow
          left={interactive ? (
            <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody><tr>
              <td style={{ padding: 0, paddingRight: 8, whiteSpace: "nowrap", verticalAlign: "middle" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                  SIP Trunk Service —
                </span>
              </td>
              <td style={{ padding: 0, verticalAlign: "middle" }}>
                {/* Freeform on purpose: "3 Line Service" is not a number. */}
                <input type="text" value={c.threeCXSipTrunkDescription || ""}
                       placeholder="e.g. 3 Line Service"
                       onChange={(e) => set({ threeCXSipTrunkDescription: e.target.value })}
                       style={addlInput} />
              </td>
            </tr></tbody></table>
          ) : (
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
              SIP Trunk Service
              {t.threeCXSipTrunkDescription ? ` — ${t.threeCXSipTrunkDescription}` : ""}
            </p>
          )}
          right={interactive ? (
            <span style={{ display: "inline-block", width: 104 }}>
              <MoneyInput value={c.threeCXSipTrunkPrice} ariaLabel="3CX SIP trunk price"
                          inputStyle={addlInput}
                          onCommit={(v) => set({ threeCXSipTrunkPrice: v })} />
            </span>
          ) : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(t.threeCXSipTrunkPrice)}/mth</p>
          )}
        />
      </div>
    ));
  }

  /* Hidden outright when nothing under it is selected — a heading over an
     empty space reads as content that failed to load. The hint is an
     instruction to the sender, so it never belongs on a customer document. */
  /* ⚠ EVERY toggle that can push a row into this section must be listed here,
     or the row prints with no heading above it. Copper and the Bronze grouping
     were added to the rows and missed here first time: both rendered, the
     "Optional Add-Ons" band did not, and the page carried two priced lines
     under whatever heading happened to precede them. */
  const anyAddOn = !!(c.addOnNetwork || c.addOnBackup || c.addOnSpamThreat
                      || c.addOnEmailSignature || c.addOnCopper || c.addOnBronze);
  if (anyAddOn || interactive)
  push("band-addons", IH.band,
    <SectionBand key="ba" title="Optional Add-Ons" hint={interactive ? "— tick to include in monthly total" : undefined}
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }} />, true);

  /* An add-on the customer did not buy prints NOTHING — no row, no
     "Not selected", no $0.00. Four unselected add-ons were costing ~230px of a
     page that clips rather than reflows. Still rendered while editing, or there
     would be no way to tick one. */
  const addOnRow = (key, h, checked, onToggle, title, sub, note, cost, last) => {
    if (!checked && !interactive) return;
    return push(key, h, (
    <div key={key} style={{ padding: "8px 24px",
                            borderBottom: `1px solid ${last ? "#e5e7eb" : "#f3f4f6"}` }}>
      <SplitRow
        left={
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                {mode !== "print" && (
                  <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14, width: 20 }}>
                    <CheckBox mode={mode} checked={checked} onToggle={onToggle} />
                  </td>
                )}
                <td style={{ verticalAlign: "middle", padding: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
                    {title}
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>{sub}</p>
                  {note && (
                    <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0" }}>{note}</p>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        }
        right={
          checked
            ? <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                          whiteSpace: "nowrap" }}>{cost}/mth</p>
            : <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, whiteSpace: "nowrap" }}>
                Not selected
              </p>
        }
      />
    </div>
  ));
  };

  /* ⚠ Placed HERE, with the other add-on rows, and not beside the device row
     above where they belong visually — `addOnRow` is a const declared further
     down this function, so calling it earlier is a temporal-dead-zone error
     that the minifier turns into a production-only "Cannot access before
     initialization". */
  addOnRow("copper", interactive ? IH.copperEdit : IH.copper, !!c.addOnCopper,
    () => set({ addOnCopper: !c.addOnCopper }),
    <>Copper Defender &mdash; Devices{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} min={0} value={t.copperDeviceCount}
                    onChange={(v) => set({ copperDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>devices</span>
      </span>
    </>,
    /* ⚠ An editable RATE, not a caption, and therefore a MoneyInput and not
       a NumberCell: NumberCell parses with parseInt and floors at 1, so it
       cannot express $15.50 and would silently rewrite it to 15. MoneyInput is
       the same control the 3CX fee and the M365 unit prices already use —
       type="text" inputMode="decimal", a $ prefix, the raw string held while
       focused and 2dp on blur. A numeric field seeded with a literal 0 is what
       produced the `010` display bug.
       Printed with fmt2, not fmt: fmt drops the cents, so a $15.50 rate would
       reach a customer document reading "$15.5". */
    interactive ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ display: "inline-block", width: 88 }}>
          <MoneyInput value={c.copperRate} ariaLabel="Copper Defender device rate"
                      inputStyle={addlInput}
                      onCommit={(v) => set({ copperRate: v })} />
        </span>
        <span>/device/mth</span>
      </span>
    ) : `${fmt2(t.copperRate)}/device/mth`,
    null, fmt(t.copperCost));

  /* Bronze devices on a Silver or Gold proposal. Never offered on Bronze
     itself, which IS the base tier — computeBronzeTotals refuses to charge for
     it there too, so the row and the total cannot disagree. */
  if (!t.isBronzeTier) addOnRow("bronzeAddOn", IH.bronzeAddOn, !!c.addOnBronze,
    () => set({ addOnBronze: !c.addOnBronze }),
    <>Bronze Sentinel &mdash; Devices{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} min={0} value={t.bronzeDeviceCount}
                    onChange={(v) => set({ bronzeDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>devices</span>
      </span>
    </>,
    `${fmt(t.perDevRate)}/device/mth — same rate as ${c.recommendedPlan} devices`,
    null, fmt(t.bronzeAddOnCost));

  addOnRow("network", IH.network, !!c.addOnNetwork,
    () => set({ addOnNetwork: !c.addOnNetwork }),
    <>Network Monitor &amp; Maintenance{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} value={t.networkDeviceCount}
                    onChange={(v) => set({ networkDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>network devices</span>
      </span>
    </>,
    `${fmt(t.networkRate)}/device/mth — ${t.isHighTier ? "Gold & Silver rate" : "Copper & Bronze rate"}`,
    null, fmt(t.networkCost));

  /* What the Network Monitor add-on actually covers, in Craig's own words.
     Descriptive only — it never touches a total. Printed only when the add-on
     is included: a device list sitting under a service the customer did not buy
     reads as a service they are getting. The editor field is likewise only
     offered when the add-on is on, so there is no invisible text to wonder about. */
  if (c.addOnNetwork && (interactive || String(c.networkMonitorDevicesCovered || "").trim()))
    push("network-covered", networkCoveredH(c.networkMonitorDevicesCovered, interactive), (
    <div key="netcov" style={{ padding: "0 24px 9px 24px", borderBottom: "1px solid #f3f4f6",
                               background: "#ffffff" }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em",
                  textTransform: "uppercase", margin: "0 0 3px" }}>
        Devices covered
      </p>
      {interactive ? (
        <textarea value={c.networkMonitorDevicesCovered || ""}
                  placeholder="e.g. 2 × FortiGate 60F, 4 × UniFi switches, 6 × access points"
                  onChange={(e) => set({ networkMonitorDevicesCovered: e.target.value })}
                  rows={2}
                  style={{ ...addlInput, width: "100%", resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <p style={{ fontSize: 12, color: "#4b5563", margin: 0, lineHeight: 1.5,
                    whiteSpace: "pre-wrap" }}>
          {c.networkMonitorDevicesCovered}
        </p>
      )}
    </div>
  ));

  addOnRow("backup", IH.backup, !!c.addOnBackup,
    () => set({ addOnBackup: !c.addOnBackup }),
    "Microsoft 365 End User Backup",
    `${t.userCount} users × ${fmt(t.backupRate)}/user/mth — email, OneDrive & SharePoint backup`,
    null, fmt(t.userCount * t.backupRate));

  addOnRow("spam", IH.spam, !!c.addOnSpamThreat,
    () => set({ addOnSpamThreat: !c.addOnSpamThreat }),
    <>Advanced AI Spam &amp; Threat Protection</>,
    `${t.userCount} users × ${fmt(t.spamThreatRate)}/user/mth`,
    "Replaces traditional Spam & Threat Protection service", fmt(t.spamThreatCost));

  addOnRow("signature", IH.signature, !!c.addOnEmailSignature,
    () => set({ addOnEmailSignature: !c.addOnEmailSignature }),
    "Email Signature Management",
    `${t.userCount} users × ${fmt(t.emailSignatureRate)}/user/mth`,
    null, fmt(t.emailSignatureCost), true);

  push("band-additional", IH.bandAction, (
    <SectionBand key="badd" title="Additional Monthly Charges"
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                 action={interactive ? (
                   <div onClick={addRow} className="no-print"
                        style={{ display: "inline-block", cursor: "pointer", padding: "4px 11px",
                                 background: "#0b80a5", borderRadius: 4 }}>
                     <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                    letterSpacing: "0.06em", textTransform: "uppercase" }}>
                       + Add Row
                     </span>
                   </div>
                 ) : null} />
  ), true);

  if (rows.length > 0) {
    /* Each additional row is its own segment, so a long list flows across pages
       instead of forcing the whole card onto one. */
    push("addl-head", IH.addlHead, (
      <table key="ah" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #f3f4f6" }}>
            <th style={{ ...addlHead, textAlign: "left", paddingLeft: 24 }}>Description</th>
            <th style={{ ...addlHead, textAlign: "center", width: 70 }}>Qty</th>
            <th style={{ ...addlHead, textAlign: "right", width: 90 }}>Unit Cost</th>
            <th style={{ ...addlHead, textAlign: "right", width: 90 }}>Total</th>
            {interactive && <th style={{ ...addlHead, width: 32, paddingRight: 24 }} />}
          </tr>
        </thead>
      </table>
    ), true);

    rows.forEach((r, i) => {
      const rowTotal = (parseFloat(r.qty) || 0) * (parseFloat(r.unitCost) || 0);
      push(`addl-${i}`, interactive ? IH.addlRowEdit : IH.addlRowPrint, (
        <table key={`ar${i}`} style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "6px 8px 6px 24px", verticalAlign: "middle" }}>
                {interactive ? (
                  <input type="text" value={r.description || ""} placeholder="e.g. Firewall Subscription"
                         onChange={(e) => patchRow(i, { description: e.target.value })}
                         style={addlInput} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>{r.description}</span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "center", verticalAlign: "middle", width: 70 }}>
                {interactive ? (
                  <input type="number" min="0" value={r.qty || ""}
                         onChange={(e) => patchRow(i, { qty: e.target.value })}
                         style={{ ...addlInput, textAlign: "center" }} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>{r.qty}</span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", verticalAlign: "middle", width: 90 }}>
                {interactive ? (
                  <input type="number" min="0" step="0.01" value={r.unitCost || ""} placeholder="0.00"
                         onChange={(e) => patchRow(i, { unitCost: e.target.value })}
                         style={{ ...addlInput, textAlign: "right" }} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>
                    {r.unitCost ? fmt2(parseFloat(r.unitCost)) : ""}
                  </span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", verticalAlign: "middle", width: 90,
                           fontSize: 13, fontWeight: 600, color: "#1a1a1a", whiteSpace: "nowrap" }}>
                {"$" + rowTotal.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              {interactive && (
                <td style={{ padding: "6px 24px 6px 8px", verticalAlign: "middle", width: 32 }}>
                  <div className="no-print" onClick={() => delRow(i)}
                       style={{ width: 24, height: 24, textAlign: "center", lineHeight: "24px",
                                cursor: "pointer", borderRadius: 4, background: "#fef2f2" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </div>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      ));
    });
  } else {
    push("addl-spacer", IH.spacer, <div key="as" style={{ height: 14, background: "#ffffff" }} />);
  }

  push("monthly-total", IH.totalBar, (
    <div key="mt" style={{ background: "#0b80a5", padding: "9px 24px" }}>
      <SplitRow
        left={<span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.9)",
                             letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Monthly Total (ex GST)</span>}
        right={<span style={{ fontSize: 22, fontWeight: 800, color: "#ffffff",
                              letterSpacing: "-0.02em" }}>{fmtMth(t.monthlyTotal)}</span>}
      />
    </div>
  ));

  /* A small clean gap lifting the whole One-Off Costs section away from the
     Monthly Total bar, so the two halves of the page read as two things rather
     than one continuous run of bars. Deliberately small — 10px, less than the
     14px used before a total — since this separates sections, it does not
     announce a final figure. Bound to the band with keepWithNext so a page
     break can never leave the gap stranded at the foot of a page. */
  push("pre-once-gap", IH.onceGap,
    <div key="pog" style={{ height: 10, background: "#ffffff" }} />, true);
  push("band-once", IH.band,
    <SectionBand key="bo" title="One-Off Costs"
                 extraStyle={{ borderTop: "2px solid #0b80a5" }} />, true);

  push("once-device", IH.onceRow, (
    <div key="od" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Onboarding Fee (per device)
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.deviceCount} devices × {fmt(t.intunePerDevRate)} — device enrolment &amp; configuration
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0, whiteSpace: "nowrap" }}>
            {fmtC(t.intunePerDevCost)}
          </p>
        }
      />
    </div>
  ));

  /* Always the taller figure. Whether this row carries the "Fee waived" pill
     depends on the template (Bronze shows it on a charged fee, Gold on a waived
     one), and when it is waived the value column grows instead — struck original
     plus $0 plus the WAIVED pill. Tracking both conditions per template invites
     exactly the inversion that under-counts height and clips the page, so take
     the maximum and accept a little whitespace in the one short case. */
  push("once-intune", IH.onceRowBadge, (
    <div key="oi" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Intune Migration &amp; Setup
            </p>
            <p style={{ fontSize: 12, color: "#4b5563", margin: "0 0 6px" }}>
              Defender setup, policy configuration &amp; security baseline — fixed fee
            </p>
            {!t.waiveSetupFees && <WaivedBadge />}
          </>
        }
        right={<FeeValue amount={t.intuneSetup} waived={t.waiveSetupFees} />}
      />
    </div>
  ));

  push("once-onboarding", IH.onceRowBadge, (
    <div key="oo" style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Initial Onboarding
            </p>
            <p style={{ fontSize: 12, color: "#4b5563", margin: "0 0 6px" }}>
              Full service transition, documentation &amp; provider handover
            </p>
            {!t.waiveSetupFees && <WaivedBadge />}
          </>
        }
        right={<FeeValue amount={t.onboarding} waived={t.waiveSetupFees} />}
      />
    </div>
  ), true);

  /* keepWithNext so the closing note is never stranded on a sheet of its own —
     a page containing one paragraph reads as a mistake. If the note has to
     flow, the Once-Off Total bar goes with it. */
  push("pre-once-spacer", IH.spacer, <div key="ps" style={{ height: 14, background: "#ffffff" }} />);
  /* Identical treatment to Monthly Total — same bar colour, same weights,
     and the same 14px of breathing room before the final number. */
  push("once-total", IH.totalBar, (
    <div key="ot" style={{ background: "#0b80a5", padding: "9px 24px" }}>
      <SplitRow
        left={<span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.9)",
                             letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Once-Off Total (ex GST)</span>}
        right={<span style={{ fontSize: 22, fontWeight: 800, color: "#ffffff",
                              letterSpacing: "-0.02em" }}>{fmtC(t.onceOffTotal)}</span>}
      />
    </div>
  ), true);

  // The closing note sits outside the card but still consumes page height, so
  // it is packed as a segment and rendered separately below.
  push("notes", IH.notes, null);

  const pages = packSegments(seg, { firstOffset: INVESTMENT_INTRO_H,
                                   capacity: INVESTMENT_CAPACITY });

  return (
    <>
      {pages.map((pageSegs, pageIdx) => {
        const cardSegs = pageSegs.filter((s) => s.key !== "notes");
        const hasNotes = pageSegs.some((s) => s.key === "notes");
        return (
          <div key={pageIdx} className="page" data-flow="investment" style={PAGE_BASE}>
            <HeaderBar customerName={c.customerName} />
            <GhostNumber n={pageIdx === 0 ? "06" : `06.${pageIdx + 1}`} />
            <div style={{ padding: "30px 64px 48px", position: "relative",
                          minHeight: 1123 - 48 - 36 }}>
              {pageIdx === 0 ? (
                <>
                  <SectionLabel text="Section 06" />
                  <h2 style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a", margin: "0 0 4px",
                               letterSpacing: "-0.02em" }}>Your IT Investment</h2>
                  <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.45 }}>
                    A clear breakdown of your ongoing managed services and one-off setup costs
                    for {c.customerName}.
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                  Your IT Investment (continued)
                </p>
              )}

              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
                            marginBottom: 14 }}>
                {cardSegs.map((s) => s.node)}
              </div>

              {hasNotes && (
                <BulletRow
                  gap={8}
                  bullet={
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"
                         strokeWidth="2" style={{ marginTop: 2 }}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  }
                >
                  <p style={{ fontSize: 12, color: "#4b5563", margin: 0, lineHeight: 1.6 }}>
                    All pricing is exclusive of GST. Quantities are based on information provided
                    by {c.customerName} — adjustments may apply following our onboarding audit.
                    This proposal is valid for 30 days. Discounts are subject to signing before
                    30 days.
                  </p>
                </BulletRow>
              )}
            </div>
            <PageFooter />
          </div>
        );
      })}
    </>
  );
}

const addlHead = {
  fontSize: 9, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
  letterSpacing: "0.08em", padding: "6px 8px",
};
const addlInput = {
  background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 4, padding: "5px 8px",
  fontSize: 13, fontFamily: "inherit", color: "#1a1a1a", outline: "none", width: "100%",
  boxSizing: "border-box",
};

/* A waived fee must read as a deliberate concession, not a missing number:
   the original struck through, $0 as the charged amount, and an explicit
   WAIVED pill. A bare $0 looks like an omission on a customer document. */
function FeeValue({ amount, waived }) {
  if (!waived) {
    return (
      <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0, whiteSpace: "nowrap" }}>
        {fmtC(amount)}
      </p>
    );
  }
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 1px",
                  textDecoration: "line-through" }}>{fmtC(amount)}</p>
      <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: "0 0 3px" }}>{fmtC(0)}</p>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                     textTransform: "uppercase", color: "#15803d", background: "#f0fdf4",
                     border: "1px solid #bbf7d0", padding: "1px 7px", borderRadius: 20 }}>
        Waived
      </span>
    </div>
  );
}

function WaivedBadge() {
  return (
    <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600, background: "#fffbeb",
                   border: "1px solid #fde68a", padding: "2px 9px", borderRadius: 20,
                   display: "inline-block" }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ verticalAlign: "middle", marginRight: 5 }}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      Fee waived when proceeding with Gold Fortress
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 8 — Response Times & Service Terms
   ══════════════════════════════════════════════════════════════════════ */
const PRIORITY_ROWS = [
  ["#ef4444", "Critical", "Main server or a network switch is offline; a VPN link between offices has failed — all or most users unable to work.", "N/A", "30 Min", "15 Min"],
  ["#f97316", "High", "Internet connection is offline (local work still possible); a key device or accounting software has stopped working.", "N/A", "2 Hours", "1 Hour"],
  ["#eab308", "Medium", "A user's desktop won't turn on; a printer isn't working but another is available; a user can't connect to Wi-Fi.", "N/A", "4 Hours", "2 Hours"],
  ["#3b82f6", "Low", "Printing is slower than normal; a single user can't scan; a user needs a program installed on their PC.", "N/A", "12 Hours", "8 Hours"],
  ["#22c55e", "No Priority", "Pro-active maintenance of systems.", "N/A", "N/A", "N/A"],
];

const TERMS = [
  "Silver Guardian requires a Microsoft 365 Premium subscription — all devices must run Windows 11 Professional/Business. Charges apply to upgrade from Windows Home or Windows 10.",
  "'Unlimited Remote Support' applies during business hours, Mon–Fri 8:30am–5pm AWST. Requests lodged outside these hours are still actioned as fast as possible, on a best-effort basis, without the response time guarantee.",
  "Ad hoc on-site support and project work is charged at $170 ex GST per hour for Silver & Gold plans (Copper & Bronze plans are charged at $190 ex GST per hour), plus a $75 ex GST call-out fee per visit.",
  "A once-off onboarding fee applies to set up Intune and Defender and enrol all devices — $110 ex GST per device, plus a fixed $599 fee to configure your M365 Intune/Defender security policy suite.",
];

function ResponseTermsPage({ c }) {
  const naCell = { fontSize: 12, color: "#9ca3af", fontWeight: 600 };
  const valCell = { fontSize: 12.5, color: "#1a1a1a", fontWeight: 700 };
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} />
      <GhostNumber n="08" />
      <div style={{ padding: "40px 64px 48px", position: "relative", minHeight: 1123 - 48 - 36 }}>
        <SectionLabel text="Section 08" />
        <h2 style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Response Times &amp; Service Terms</h2>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 18px", lineHeight: 1.45 }}>
          Our commitment to fast, reliable support — and the terms that keep it fair for{" "}
          {c.customerName} and every one of our clients.
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 20 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 14px 8px 0", width: "14%" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a",
                               letterSpacing: "0.08em", textTransform: "uppercase" }}>Priority</span>
                <div style={{ width: 22, height: 2, background: "#0b80a5", marginTop: 4 }} />
              </th>
              <th style={{ textAlign: "left", padding: "8px 14px", width: "44%" }}>
                <span style={thMuted}>Examples</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%", background: "#f8fafc" }}>
                <span style={{ ...thMuted, fontSize: 9.5, lineHeight: 1.3 }}>Copper &amp; Bronze</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%",
                           background: "rgba(11,128,165,0.07)" }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Silver</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%" }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Gold Fortress</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {PRIORITY_ROWS.map(([color, name, example, cb, silver, gold], i) => (
              <tr key={name}
                  style={{ borderTop: "1px solid #f0f0f0",
                           borderBottom: i === PRIORITY_ROWS.length - 1 ? "1px solid #f0f0f0" : undefined }}>
                <td style={{ padding: "10px 14px 10px 0", verticalAlign: "top" }}>
                  <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                                 background: color, marginRight: 7, verticalAlign: "middle" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a", verticalAlign: "middle" }}>
                    {name}
                  </span>
                </td>
                <td style={{ padding: "10px 14px", verticalAlign: "top", fontSize: 11.5,
                             color: "#4b5563", lineHeight: 1.45 }}>{example}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: "#f8fafc", ...naCell }}>{cb}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: "rgba(11,128,165,0.07)", ...(silver === "N/A" ? naCell : valCell) }}>
                  {silver}
                </td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             ...(gold === "N/A" ? naCell : valCell) }}>{gold}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "8px 20px", background: "#1f2937", borderLeft: "4px solid #0b80a5" }}>
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em", color: "#ffffff",
                        textTransform: "uppercase", margin: 0 }}>Basic Terms &amp; Conditions</p>
          </div>
          <div style={{ padding: "14px 20px" }}>
            {TERMS.map((term, i) => (
              <BulletRow key={i} bullet={dot()} bottom={i === TERMS.length - 1 ? 0 : 10}>
                <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.55, margin: 0 }}>{term}</p>
              </BulletRow>
            ))}
          </div>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

const thMuted = {
  fontSize: 10, fontWeight: 700, color: "#6b7280",
  letterSpacing: "0.08em", textTransform: "uppercase",
};

/* ══════════════════════════════════════════════════════════════════════
   PAGE 9 — Foundational Upgrade Plan
   ══════════════════════════════════════════════════════════════════════ */
/* One card. Extracted so the page loop stays readable; `num` is the block's
   GLOBAL position across the whole section, not its index on this page. */
/* One FRAGMENT of a block's card. A block that fits renders as a single
   fragment and looks exactly as it always did; a long one renders as several,
   each a closed rounded card, each continuation re-announcing which block it
   belongs to and repeating the column headers. */
function FoundationalBlockCard({ block, num, items: fragItems, isFirst = true,
                                 isLast = true, continuesBelow = false }) {
  const items = Array.isArray(fragItems)
    ? fragItems
    : (Array.isArray(block.lineItems) ? block.lineItems : []);
  const label = String(num).padStart(2, "0");
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 16,
                  position: "relative", overflow: "hidden", marginBottom: 12 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3,
                    background: "#0b80a5" }} />
      {isFirst ? (
        <>
      <SplitRow
        align="top"
        style={{ marginBottom: 10 }}
        left={
          <div style={{ width: 28, height: 28, background: "#1a1a1a", borderRadius: 6,
                        textAlign: "center", lineHeight: "28px" }}>
            <span style={{ color: "#0b80a5", fontSize: 11, fontWeight: 800,
                           letterSpacing: "-0.02em" }}>
              {String(num).padStart(2, "0")}
            </span>
          </div>
        }
        right={
          <span style={{ fontSize: 9, fontWeight: 600, color: "#4b5563",
                         background: "#f3f4f6", padding: "2px 7px", borderRadius: 20,
                         letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Quoted Separately
          </span>
        }
      />

      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", margin: "0 0 6px",
                   letterSpacing: "-0.01em" }}>{block.title}</h3>
      {block.description && (
        <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, margin: "0 0 8px" }}>
          {block.description}
        </p>
      )}
      {block.timeline && (
        <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 8px", fontStyle: "italic" }}>
          {block.timeline}
        </p>
      )}
        </>
      ) : (
        /* Continuation header: same block number and title, marked as carried
           over. No description, timeline or QUOTED SEPARATELY badge — those
           belong to the block's opening, and repeating them would read as a
           second, near-identical project. */
        <div style={{ marginBottom: 8 }}>
          <table style={{ borderCollapse: "collapse" }}><tbody><tr>
            <td style={{ padding: 0, paddingRight: 10, verticalAlign: "middle" }}>
              <div style={{ width: 28, height: 28, background: "#1a1a1a", borderRadius: 6,
                            textAlign: "center", lineHeight: "28px" }}>
                <span style={{ color: "#0b80a5", fontSize: 11, fontWeight: 800,
                               letterSpacing: "-0.02em" }}>{label}</span>
              </div>
            </td>
            <td style={{ padding: 0, verticalAlign: "middle" }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a",
                             letterSpacing: "-0.01em" }}>
                {label} (cont.) — {block.title}
              </span>
            </td>
          </tr></tbody></table>
        </div>
      )}

      {items.length > 0 && (
        <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse", marginTop: 4 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={fbTh}>Item</th>
              <th style={{ ...fbTh, textAlign: "center", width: 44 }}>Qty</th>
              <th style={{ ...fbTh, textAlign: "right", width: 76 }}>Price</th>
              <th style={{ ...fbTh, textAlign: "right", width: 84 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, j) => (
              <tr key={li.id || j} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "5px 0", color: "#1f2937", verticalAlign: "top" }}>
                  <span style={{ fontWeight: 600 }}>{li.description}</span>
                  {li.manufacturer && (
                    <span style={{ color: "#9ca3af" }}> · {li.manufacturer}</span>
                  )}
                  {li.detail && (
                    <div style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.45,
                                  marginTop: 2 }}>{li.detail}</div>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "center", color: "#4b5563",
                             verticalAlign: "top" }}>{li.quantity}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#4b5563",
                             verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {fmtMoney(li.unitPrice)}
                </td>
                <td style={{ padding: "5px 0", textAlign: "right", fontWeight: 600,
                             color: "#1a1a1a", verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {fmtMoney(lineItemTotal(li))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The subtotal belongs to the block, so it prints once, on the fragment
          that closes it — never repeated per page and never left on its own. */}
      {isLast ? (
        <SplitRow
          style={{ marginTop: 8 }}
          left={null}
          right={
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a",
                           whiteSpace: "nowrap" }}>
              Subtotal (ex GST): {fmtMoney(computeBlockSubtotal(block))}
            </span>
          }
        />
      ) : continuesBelow ? (
        <p style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic",
                    margin: "6px 0 0", textAlign: "right" }}>
          continued on next page →
        </p>
      ) : null}
    </div>
  );
}

const fbTh = {
  padding: "4px 0", textAlign: "left", fontSize: 9, fontWeight: 700,
  color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase",
};

/* Returns one OR MORE pages. Foundational is the only section whose content is
   unbounded, so it packs into as many fixed-height page divs as it needs —
   the same "one div = one Prince page" pattern every other page already uses,
   just repeated. Each continuation page carries its own header bar, so the
   header repeats without needing Prince running-elements. */
function FoundationalPage({ c }) {
  const blocks = Array.isArray(c.foundationalBlocks) ? c.foundationalBlocks : [];
  /* The note is passed in because it consumes first-page height. Packing
     without it would let a block overflow a page that still believed it had
     room, and `.page` is overflow:hidden — the block would vanish, not
     reflow. Same rule as the title: first page only. */
  const pricingNote = String(c.foundationalPricingNote || "").trim();
  const pages = chunkFoundationalBlocks(blocks, pricingNote);

  return (
    <>
      {pages.map((pageBlocks, pageIdx) => (
        <div key={pageIdx} className="page" data-flow="foundational" style={PAGE_BASE}>
          <HeaderBar customerName={c.customerName} />
          <GhostNumber n={pageIdx === 0 ? "07" : `07.${pageIdx + 1}`} />
          <div style={{ padding: "44px 64px 52px", position: "relative",
                        minHeight: 1123 - 48 - 36 }}>
            {pageIdx === 0 ? (
              <>
                <SectionLabel text="Section 07" />
                <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                             letterSpacing: "-0.02em" }}>Foundational Upgrade Plan</h2>
                <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
                  These one-off projects address the critical gaps identified during our
                  assessment. Each is quoted separately and can be staged based on priority
                  and budget.
                </p>
                {/* Estimate-only caveat. Same small grey italic treatment as the
                    Investment page's GST line — reused, not reinvented. Suppressed
                    entirely when blank so an emptied field leaves no gap. */}
                {pricingNote && (
                  <p style={{ fontSize: 11.5, color: "#4b5563", margin: "0 0 16px",
                              fontStyle: "italic", lineHeight: 1.5 }}>
                    {pricingNote}
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic",
                          margin: "0 0 16px" }}>
                Foundational Upgrade Plan (continued)
              </p>
            )}

            {blocks.length === 0 ? (
              <EmptyBlock title="No projects added yet"
                          hint="Open the settings panel → Foundational Upgrade → Add Block"
                          pad={40} />
            ) : (
              /* Fragments, not blocks: a long block arrives as several, each
                 already carrying its own items, header kind and closing state.
                 The number comes from the fragment so a continuation keeps its
                 block's number rather than taking the next one. */
              pageBlocks.map((frag, fi) => (
                <FoundationalBlockCard
                  key={`${frag.block.id || frag.num}-${fi}`}
                  block={frag.block} num={frag.num} items={frag.items}
                  isFirst={frag.isFirst} isLast={frag.isLast}
                  continuesBelow={frag.continuesBelow} />
              ))
            )}
          </div>
          <PageFooter />
        </div>
      ))}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 10 — Why Choose Datatech
   Fixed height (not min-height) in the source, with its own dark contact
   footer instead of the standard thin one.
   ══════════════════════════════════════════════════════════════════════ */
const STATS = [
  ["20+",  "Years Experience"],
  ["75+",  "WA Businesses"],
  ["24/7", "SOC Monitoring"],
  ["2hr",  "SLA Response"],
];

const DIFFERENTIATORS = [
  ["Complete Technology Partner", "IT support is just the beginning. We cover phones, internet, CCTV, Wi-Fi, access control, digital signage, and meeting rooms — all under one expert provider."],
  ["Built on Trust", "We treat your business and budget as if they were our own. We own our mistakes, always strive for honest service, and hold ourselves to the highest standard of accountability."],
  ["An Extension of Your Team", "Most of our clients consider us part of their own company. We work side by side with your staff — providing expert support, proactive problem solving, and guidance aligned to your goals."],
  ["Local Perth Team", "We're based in Perth, but service customers throughout WA from the Kimberley to the Great Southern.  We understand WA businesses. We know local compliance, local connectivity challenges, and what it means to be a genuinely committed local technology partner."],
];

function WhyUsPage({ c, assetBase }) {
  return (
    <div className="page" style={{ ...PAGE_BASE, height: 1123, minHeight: 1123 }}>
      <div style={{ position: "relative", zIndex: 3 }}>
        <HeaderBar customerName={c.customerName} />
      </div>

      <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400,
                    borderRadius: "50%", background: "rgba(11,128,165,0.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 90, left: -60, width: 280, height: 280,
                    borderRadius: "50%", background: "rgba(11,128,165,0.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)",
                    backgroundSize: "22px 22px", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, zIndex: 2,
                    background: "linear-gradient(90deg, #0b80a5, #2a4365)" }} />

      <div style={{ padding: "28px 64px 32px", position: "relative", zIndex: 1 }}>
        {/* Stats row — 1px gaps via border-spacing over a dark table bg */}
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 1,
                        background: "#1a1a1a", borderRadius: 10, overflow: "hidden",
                        marginBottom: 40, tableLayout: "fixed" }}>
          <tbody>
            <tr>
              {STATS.map(([value, label]) => (
                <td key={label} style={{ background: "#222222", padding: "22px 20px", textAlign: "center" }}>
                  <p style={{ fontSize: 32, fontWeight: 800, color: "#0b80a5", margin: 0,
                              letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</p>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", margin: "6px 0 0",
                              letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500 }}>
                    {label}
                  </p>
                </td>
              ))}
            </tr>
          </tbody>
        </table>

        <SectionLabel text="Section 09" />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Why Choose Datatech?</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 28px", lineHeight: 1.5 }}>
          We're more than an IT provider — we're your complete technology partner.
        </p>

        <TwoColTable
          items={DIFFERENTIATORS}
          colGap={14}
          rowGap={14}
          style={{ marginBottom: 32 }}
          renderItem={([title, body]) => (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)",
                          borderLeft: "3px solid #0b80a5", borderRadius: 8, padding: "20px 22px",
                          height: "100%" }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", margin: "0 0 8px",
                           letterSpacing: "-0.01em" }}>{title}</h3>
              <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.6)", lineHeight: 1.6, margin: 0 }}>
                {body}
              </p>
            </div>
          )}
        />
      </div>

      {/* Contact footer — absolute to PAGE, not the content div */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "#1a1a1a",
                    padding: "24px 64px", zIndex: 3 }}>
        <SplitRow
          left={
            <>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "0 0 10px" }}>
                Ready to take the next step? Get in touch with your Datatech team.
              </p>
              <table style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 28, whiteSpace: "nowrap" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0b80a5"
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                           style={{ verticalAlign: "middle", marginRight: 8 }}>
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 010 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                      </svg>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: "#ffffff",
                                     verticalAlign: "middle" }}>(08) 6102 2037</span>
                    </td>
                    <td style={{ verticalAlign: "middle", padding: 0, whiteSpace: "nowrap" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0b80a5"
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                           style={{ verticalAlign: "middle", marginRight: 8 }}>
                        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                      </svg>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: "#ffffff",
                                     verticalAlign: "middle" }}>datatech.net.au</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          }
          right={
            <img src={`${assetBase}/proposal-assets/logo-dark-bg.png`} alt="Datatech Solutions"
                 style={{ height: 44, objectFit: "contain", borderRadius: 6 }} />
          }
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   The template
   ══════════════════════════════════════════════════════════════════════ */
// assetBase: prefix for the two logo files in public/proposal-assets.
// Empty on screen (root-relative is fine in the browser). For mode="print" the
// caller MUST pass an absolute origin — DocRaptor fetches images server-side
// and cannot resolve "/proposal-assets/...".
export default function BronzeTemplate({ content, mode = "edit", onChange, assetBase = "", onCoverImage }) {
  const c = { ...DEFAULT_BRONZE_CONTENT(), ...(content || {}) };
  const set = React.useCallback(
    (patch) => { if (onChange) onChange(patch); },
    [onChange]
  );
  const t = computeBronzeTotals(c);

  const listPages = {
    challenges: {
      key: "challenges", ghost: "02", section: "Section 02", heading: "What We Heard",
      subtitle: (name) => `Key challenges and observations shared during my discovery session with ${name}.`,
      emptyTitle: "No challenges added yet",
      emptyHint: "Open the settings panel → What We Heard → Add Challenge",
      addLabel: "Add Challenge",
      icons: CHALLENGE_ICONS, accent: "#0b80a5", iconBg: "#eff6ff", iconStroke: "#0b80a5",
    },
    findings: {
      key: "findings", ghost: "03", section: "Section 03", heading: "What We've Found",
      subtitle: (name) => `Our technical assessment findings following our review of ${name}'s environment.`,
      emptyTitle: "No findings added yet",
      emptyHint: "Open the settings panel → What We've Found → Add Finding",
      addLabel: "Add Finding",
      icons: FINDING_ICONS, accent: "#1a1a1a", iconBg: "#f1f5f9", iconStroke: "#374151",
    },
  };

  return (
    <div className="proposal-bronze doc-pages" data-mode={mode}>
      <CoverPage c={c} mode={mode} set={set} assetBase={assetBase} onCoverImage={onCoverImage} />
      {c.showIntro !== false        && <IntroPage c={c} mode={mode} set={set} />}
      {c.showWhatHeard !== false    && <ListCardPage c={c} mode={mode} set={set} cfg={listPages.challenges} />}
      {c.showWhatFound !== false    && <ListCardPage c={c} mode={mode} set={set} cfg={listPages.findings} />}
      {c.showRecommend !== false    && <RecommendPage c={c} mode={mode} t={t} />}
      {c.showComparison !== false   && <ComparisonPage c={c} />}
      {c.showInvestment !== false   && <InvestmentPage c={c} mode={mode} set={set} t={t} />}
      {c.showFoundational !== false && <FoundationalPage c={c} mode={mode} set={set} />}
      {c.showResponseTerms !== false && <ResponseTermsPage c={c} />}
      {c.showWhyUs !== false        && <WhyUsPage c={c} assetBase={assetBase} />}
    </div>
  );
}
